import { Component } from '@theme/component';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const CART_ADD_REQUEST_PATTERN = /\/cart\/add(?:\.js)?(?:[/?#]|$)/i;
const TELEMETRY_REQUEST_PATTERNS = [
  /\/api\/collect(?:[/?#]|$)/i,
  /\/api\/produce_batch(?:[/?#]|$)/i,
  /shopifysvc\.com\/v1\/metrics/i,
];
const BODY_MODAL_OPEN_CLASS = 'engraving-personalizer-modal-open';
const XHR_REQUEST_META = Symbol('engravingRequestMeta');

/**
 * @typedef {object} EngravingInjectionLine
 * @property {number} slot
 * @property {string} text
 * @property {string} color
 * @property {string} configId
 * @property {string} variantId
 * @property {boolean} [used]
 */

/**
 * @typedef {object} PendingEngravingInjection
 * @property {number} expiresAt
 * @property {string} visiblePropertyKey
 * @property {string} bundleGroupId
 * @property {string} engravingGroupId
 * @property {string} bundlePlan
 * @property {string} previewVersion
 * @property {EngravingInjectionLine[]} lines
 */

/** @type {{ installed: boolean, pending: PendingEngravingInjection | null }} */
const engravingCartInjectionState = {
  installed: false,
  pending: null,
};

function installEngravingCartHooks() {
  if (engravingCartInjectionState.installed) return;

  engravingCartInjectionState.installed = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    let nextInit = init;
    const requestUrl = getRequestUrl(input);

    try {
      const requestMethod = getRequestMethod(input, init);

      if (isCartAddRequest(requestUrl, requestMethod) && nextInit?.body != null) {
        const { body, didMutate } = injectEngravingIntoBody(nextInit.body);
        if (didMutate) {
          nextInit = {
            ...nextInit,
            body,
          };
        }
      }
    } catch (error) {
      console.error('Failed to inject engraving properties into fetch request', error);
    }

    return originalFetch(input, nextInit).catch((error) => {
      if (isIgnorableTelemetryRequest(requestUrl, error)) {
        return new Response('{}', {
          status: 204,
          headers: {
            'Content-Type': 'application/json',
          },
        });
      }

      throw error;
    });
  };

  const originalXhrOpen = XMLHttpRequest.prototype.open;
  const originalXhrSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function open(method, url, ...rest) {
    this[XHR_REQUEST_META] = {
      method: (method || 'GET').toString(),
      url: typeof url === 'string' ? url : String(url || ''),
    };
    return originalXhrOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function send(body) {
    let nextBody = body;

    try {
      const meta = this[XHR_REQUEST_META];
      if (meta && isCartAddRequest(meta.url, meta.method) && nextBody != null) {
        const { body: injectedBody, didMutate } = injectEngravingIntoBody(nextBody);
        if (didMutate) {
          nextBody = injectedBody;
        }
      }
    } catch (error) {
      console.error('Failed to inject engraving properties into XHR request', error);
    }

    return originalXhrSend.call(this, nextBody);
  };
}

/**
 * @param {string} url
 * @param {unknown} error
 * @returns {boolean}
 */
function isIgnorableTelemetryRequest(url, error) {
  if (!url) return false;

  let normalizedUrl = url;
  try {
    normalizedUrl = new URL(url, window.location.origin).toString();
  } catch (resolveError) {
    normalizedUrl = String(url || '');
  }

  const matchesTelemetryPattern = TELEMETRY_REQUEST_PATTERNS.some((pattern) => pattern.test(normalizedUrl));
  if (!matchesTelemetryPattern) return false;

  const message = (error instanceof Error ? error.message : String(error || '')).toLowerCase();
  const errorName = (error && typeof error === 'object' && 'name' in error ? String(error.name) : '').toLowerCase();
  const stack = (error && typeof error === 'object' && 'stack' in error ? String(error.stack) : '').toLowerCase();

  return (
    message.includes('failed to fetch') ||
    message.includes('blocked') ||
    message.includes('network') ||
    message.includes('load failed') ||
    message.includes('err_blocked_by_client') ||
    errorName.includes('abort') ||
    stack.includes('err_blocked_by_client')
  );
}

/**
 * @param {string | URL | Request | unknown} input
 * @returns {string}
 */
function getRequestUrl(input) {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  if (input instanceof Request) return input.url || '';
  if (input && typeof input === 'object' && 'url' in input) {
    return String(input.url || '');
  }
  return '';
}

/**
 * @param {string | URL | Request | unknown} input
 * @param {RequestInit | undefined} init
 * @returns {string}
 */
function getRequestMethod(input, init) {
  const method = init?.method || (input instanceof Request ? input.method : 'GET');
  return (method || 'GET').toString().toUpperCase();
}

/**
 * @param {string} url
 * @param {string} method
 * @returns {boolean}
 */
function isCartAddRequest(url, method) {
  if (method !== 'POST') return false;
  if (!url) return false;

  try {
    const absoluteUrl = new URL(url, window.location.origin);
    return CART_ADD_REQUEST_PATTERN.test(absoluteUrl.pathname);
  } catch (error) {
    return CART_ADD_REQUEST_PATTERN.test(url);
  }
}

/**
 * @param {BodyInit | null | undefined} body
 * @returns {{ body: BodyInit | null | undefined, didMutate: boolean }}
 */
function injectEngravingIntoBody(body) {
  const pending = getPendingEngravingInjection();
  if (!pending) {
    return { body, didMutate: false };
  }

  if (typeof body === 'string') {
    const jsonResult = tryInjectIntoJsonBody(body, pending);
    if (jsonResult.didMutate) {
      finalizePendingEngravingInjection();
      return jsonResult;
    }

    const paramsResult = tryInjectIntoUrlEncodedBody(body, pending);
    if (paramsResult.didMutate) {
      finalizePendingEngravingInjection();
      return paramsResult;
    }

    return { body, didMutate: false };
  }

  if (body instanceof FormData) {
    const didMutate = injectIntoFormData(body, pending);
    if (didMutate) {
      finalizePendingEngravingInjection();
    }
    return { body, didMutate };
  }

  if (body instanceof URLSearchParams) {
    const didMutate = injectIntoUrlSearchParams(body, pending);
    if (didMutate) {
      finalizePendingEngravingInjection();
    }
    return { body, didMutate };
  }

  return { body, didMutate: false };
}

/**
 * @param {string} jsonString
 * @param {PendingEngravingInjection} pending
 * @returns {{ body: string, didMutate: boolean }}
 */
function tryInjectIntoJsonBody(jsonString, pending) {
  let parsed;

  try {
    parsed = JSON.parse(jsonString);
  } catch (error) {
    return { body: jsonString, didMutate: false };
  }

  if (!parsed || typeof parsed !== 'object') {
    return { body: jsonString, didMutate: false };
  }

  const didMutate = injectIntoCartPayload(parsed, pending);
  if (!didMutate) {
    return { body: jsonString, didMutate: false };
  }

  return {
    body: JSON.stringify(parsed),
    didMutate: true,
  };
}

/**
 * @param {string} encodedString
 * @param {PendingEngravingInjection} pending
 * @returns {{ body: string, didMutate: boolean }}
 */
function tryInjectIntoUrlEncodedBody(encodedString, pending) {
  const params = new URLSearchParams(encodedString);
  const didMutate = injectIntoUrlSearchParams(params, pending);

  return {
    body: didMutate ? params.toString() : encodedString,
    didMutate,
  };
}

/**
 * @param {Record<string, any>} payload
 * @param {PendingEngravingInjection} pending
 * @returns {boolean}
 */
function injectIntoCartPayload(payload, pending) {
  if (!payload || typeof payload !== 'object') return false;

  let didMutate = false;

  if (Array.isArray(payload.items)) {
    for (const item of payload.items) {
      if (!item || typeof item !== 'object') continue;
      if (injectIntoCartItem(item, pending)) {
        didMutate = true;
      }
    }

    return didMutate;
  }

  if (Object.prototype.hasOwnProperty.call(payload, 'id') || Object.prototype.hasOwnProperty.call(payload, 'variant_id')) {
    return injectIntoCartItem(payload, pending);
  }

  return false;
}

/**
 * @param {Record<string, any>} item
 * @param {PendingEngravingInjection} pending
 * @returns {boolean}
 */
function injectIntoCartItem(item, pending) {
  const variantId = normalizeVariantId(item.id ?? item.variant_id ?? item.variantId);
  const quantity = toPositiveInt(item.quantity, 1);
  const lines = takeInjectionLines(pending, variantId, quantity);
  if (lines.length === 0) return false;

  const properties = item.properties && typeof item.properties === 'object' ? item.properties : {};
  writeLineProperties(properties, pending, lines);
  item.properties = properties;

  return true;
}

/**
 * @param {FormData} formData
 * @param {PendingEngravingInjection} pending
 * @returns {boolean}
 */
function injectIntoFormData(formData, pending) {
  const variantId = normalizeVariantId(formData.get('id') || formData.get('variant_id'));
  if (!variantId) return false;

  const quantity = toPositiveInt(formData.get('quantity'), 1);
  const lines = takeInjectionLines(pending, variantId, quantity);
  if (lines.length === 0) return false;

  /** @type {Record<string, string>} */
  const properties = {};
  writeLineProperties(properties, pending, lines);

  for (const [key, value] of Object.entries(properties)) {
    formData.set(`properties[${key}]`, value);
  }

  return true;
}

/**
 * @param {URLSearchParams} params
 * @param {PendingEngravingInjection} pending
 * @returns {boolean}
 */
function injectIntoUrlSearchParams(params, pending) {
  const variantId = normalizeVariantId(params.get('id') || params.get('variant_id'));
  if (!variantId) return false;

  const quantity = toPositiveInt(params.get('quantity'), 1);
  const lines = takeInjectionLines(pending, variantId, quantity);
  if (lines.length === 0) return false;

  /** @type {Record<string, string>} */
  const properties = {};
  writeLineProperties(properties, pending, lines);

  for (const [key, value] of Object.entries(properties)) {
    params.set(`properties[${key}]`, value);
  }

  return true;
}

/**
 * @param {Record<string, any>} target
 * @param {PendingEngravingInjection} pending
 * @param {EngravingInjectionLine[]} lines
 */
function writeLineProperties(target, pending, lines) {
  if (lines.length === 0) return;

  const mergedText = lines.map((line) => line.text).join(' | ');
  const mergedSlots = lines.map((line) => String(line.slot)).join(',');
  const mergedColors = lines.map((line) => line.color).join(', ');

  target[pending.visiblePropertyKey] = mergedText;
  target._personalized = '1';
  target._engraving_text = mergedText;
  target._engraving_slot = mergedSlots;
  target._engraving_color = mergedColors;
  target._engraving_config_id = lines[0].configId || '';
  target._bundle_group_id = pending.bundleGroupId;
  target._engraving_group_id = pending.engravingGroupId;
  target._bundle_plan = pending.bundlePlan;
  target._preview_version = pending.previewVersion;

  for (const line of lines) {
    target[`_engraving_text_${line.slot}`] = line.text;
    target[`_engraving_color_${line.slot}`] = line.color;
    target[`_engraving_config_id_${line.slot}`] = line.configId;
  }
}

/**
 * @param {PendingEngravingInjection} pending
 * @param {string} variantId
 * @param {number} quantity
 * @returns {EngravingInjectionLine[]}
 */
function takeInjectionLines(pending, variantId, quantity) {
  const lines = [];
  const targetCount = Math.max(1, quantity);

  for (let i = 0; i < targetCount; i += 1) {
    const nextLine = takeSingleInjectionLine(pending, variantId);
    if (!nextLine) break;
    lines.push(nextLine);
  }

  return lines;
}

/**
 * @param {PendingEngravingInjection} pending
 * @param {string} variantId
 * @returns {EngravingInjectionLine | null}
 */
function takeSingleInjectionLine(pending, variantId) {
  const normalizedVariantId = normalizeVariantId(variantId);
  let lineIndex = -1;

  if (normalizedVariantId) {
    lineIndex = pending.lines.findIndex((line) => !line.used && normalizeVariantId(line.variantId) === normalizedVariantId);
  }

  if (lineIndex === -1) {
    lineIndex = pending.lines.findIndex((line) => !line.used);
  }

  if (lineIndex === -1) return null;

  const line = pending.lines[lineIndex];
  line.used = true;
  return line;
}

function getPendingEngravingInjection() {
  const pending = engravingCartInjectionState.pending;
  if (!pending) return null;

  if (Date.now() > pending.expiresAt) {
    engravingCartInjectionState.pending = null;
    return null;
  }

  return pending;
}

function finalizePendingEngravingInjection() {
  const pending = engravingCartInjectionState.pending;
  if (!pending) return;

  const hasUnusedLines = pending.lines.some((line) => !line.used);
  if (!hasUnusedLines) {
    engravingCartInjectionState.pending = null;
  }
}

/**
 * @param {{
 *   visiblePropertyKey: string,
 *   bundleGroupId: string,
 *   bundlePlan: string,
 *   previewVersion: string,
 *   lines: EngravingInjectionLine[]
 * }} payload
 */
function queueEngravingCartInjection(payload) {
  const filteredLines = payload.lines
    .map((line) => ({
      slot: toPositiveInt(line.slot, 1),
      text: (line.text || '').trim(),
      color: (line.color || '').trim(),
      configId: (line.configId || '').trim(),
      variantId: normalizeVariantId(line.variantId),
      used: false,
    }))
    .filter((line) => line.text.length > 0);

  if (filteredLines.length === 0) {
    engravingCartInjectionState.pending = null;
    return;
  }

  engravingCartInjectionState.pending = {
    expiresAt: Date.now() + 15_000,
    visiblePropertyKey: payload.visiblePropertyKey || 'Gravur',
    bundleGroupId: payload.bundleGroupId || '',
    bundlePlan: payload.bundlePlan || 'bundle',
    previewVersion: payload.previewVersion || 'v1',
    lines: filteredLines,
  };
}

function clearEngravingCartInjection() {
  engravingCartInjectionState.pending = null;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normalizeVariantId(value) {
  if (value == null) return '';
  const rawValue = String(value).trim();
  if (!rawValue) return '';

  if (/^\d+$/.test(rawValue)) return rawValue;

  const gidMatch = rawValue.match(/ProductVariant\/(\d+)/i);
  if (gidMatch) return gidMatch[1];

  const numericMatch = rawValue.match(/\b(\d{8,})\b/);
  if (numericMatch) return numericMatch[1];

  return rawValue;
}

/**
 * @param {unknown} value
 * @param {number} fallback
 * @returns {number}
 */
function toPositiveInt(value, fallback) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.floor(parsed);
}

/**
 * @typedef {object} EngravingLayout
 * @property {string} [configId]
 * @property {string} [assetBase]
 * @property {number|string} [canvasWidth]
 * @property {number|string} [canvasHeight]
 * @property {number|string} [textX]
 * @property {number|string} [textY]
 * @property {number|string} [textRotationDeg]
 * @property {number|string} [textMaxWidth]
 * @property {string} [fontFamily]
 * @property {number|string} [fontSizePx]
 * @property {number|string} [letterSpacingEm]
 * @property {string} [textColor]
 * @property {string} [blendMode]
 * @property {number|string} [textOpacity]
 */

/**
 * @typedef {object} EngravingPersonalizerRefs
 * @property {HTMLInputElement} textInput
 * @property {HTMLElement} characterCount
 * @property {HTMLInputElement} visibleProperty
 * @property {HTMLInputElement} personalizedProperty
 * @property {HTMLInputElement} engravingTextProperty
 * @property {HTMLInputElement} engravingSlotProperty
 * @property {HTMLInputElement} engravingConfigIdProperty
 * @property {HTMLInputElement} bundleGroupIdProperty
 * @property {HTMLInputElement} bundlePlanProperty
 * @property {HTMLInputElement} previewVersionProperty
 * @property {HTMLElement} previewContainer
 * @property {HTMLImageElement} previewImage
 * @property {SVGElement} previewSvg
 * @property {SVGTextElement} previewText
 * @property {HTMLScriptElement} variantEngravingMap
 * @property {HTMLScriptElement} variantOptionMap
 * @property {HTMLElement} slotOneLabel
 * @property {HTMLButtonElement} openPersonalizerButton
 * @property {HTMLElement} launcherTitle
 * @property {HTMLElement} launcherSummary
 * @property {HTMLElement} launcherActiveBadge
 * @property {HTMLElement} modal
 * @property {HTMLElement} [bundleSelectorContainer]
 * @property {HTMLInputElement[]} [bundlePlanInputs]
 * @property {HTMLInputElement[]} [bundleSlotInputs]
 * @property {HTMLSelectElement[]} [bundleSlotVariantSelects]
 * @property {HTMLInputElement[]} [bundleVisibleSlotProperties]
 * @property {HTMLInputElement[]} [bundleSlotTextProperties]
 * @property {HTMLInputElement[]} [bundleSlotColorProperties]
 * @property {HTMLInputElement[]} [bundleSlotConfigProperties]
 */

class EngravingPersonalizerComponent extends Component {
  requiredRefs = [
    'textInput',
    'characterCount',
    'visibleProperty',
    'personalizedProperty',
    'engravingTextProperty',
    'engravingSlotProperty',
    'engravingConfigIdProperty',
    'bundleGroupIdProperty',
    'bundlePlanProperty',
    'previewVersionProperty',
    'previewContainer',
    'previewImage',
    'previewSvg',
    'previewText',
    'variantEngravingMap',
    'variantOptionMap',
    'slotOneLabel',
    'openPersonalizerButton',
    'launcherTitle',
    'launcherSummary',
    'launcherActiveBadge',
    'modal',
  ];

  /** @type {HTMLElement | Document | null} */
  #eventTarget = null;

  /** @type {Record<string, EngravingLayout>} */
  #engravingByVariant = {};

  /** @type {EngravingLayout} */
  #defaultLayout = {};

  /** @type {string} */
  #bundleGroupId = '';

  /** @type {string | null} */
  #activeVariantId = null;

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {boolean} */
  #isSubmittingBundle = false;

  /** @type {boolean} */
  #isModalOpen = false;

  /** @type {{ planQty: number, slotValues: string[] } | null} */
  #draftState = null;

  /** @type {string} */
  #previousBodyOverflow = '';

  /** @type {number} */
  #textMaxWidth = 0;

  /** @type {Record<string, {title?: string, option1?: string, option2?: string, option3?: string}>} */
  #variantOptionsById = {};

  /** @type {string[]} */
  #bundleSlotColors = [];

  /** @type {string[]} */
  #bundleSlotVariantIds = [];

  /** @type {HTMLElement | null} */
  #externalBundleRoot = null;

  /** @type {boolean} */
  #useExternalBundleApp = false;

  /** Slots whose colour was explicitly chosen by the customer (kept across variant changes). */
  #touchedSwatchSlots = new Set();

  /** @type {number} */
  #activePreviewSlot = 1;

  /** @type {number} */
  #selectedPlanQty = 1;

  /** @type {boolean} */
  #isSyncingEngravingFees = false;

  /** @type {Set<string>} */
  #suppressedFeeGroupIds = new Set();

  /** @type {Map<string, { personalizedQty: number, feeQty: number }>} */
  #lastEngravingGroupSnapshot = new Map();

  /** @type {HTMLElement | null} */
  #externalBundleTotalNode = null;

  /** @type {HTMLElement | null} */
  #externalBundleTotalValueNode = null;

  /** @type {MutationObserver | null} */
  #externalBundleTotalObserver = null;

  /** @type {string} */
  #externalBundleTotalBaseText = '';

  /** @type {string} */
  #externalBundleTotalLastRenderedText = '';

  /** @type {{ prefix: string, suffix: string, cents: number, decimals: number, decimalSeparator: string, groupSeparator: string } | null} */
  #externalBundleTotalTemplate = null;

  /** @type {boolean} */
  #isWritingExternalBundleTotal = false;

  #onCartChange = (event) => {
    const cartItems = event?.detail?.cart?.items;
    if (Array.isArray(cartItems)) {
      this.#trackFeeSuppression(cartItems);
    }

    if (this.#isSyncingEngravingFees) return;
    void this.#syncEngravingFees();
  };

  connectedCallback() {
    super.connectedCallback();

    installEngravingCartHooks();

    this.#parseEngravingMap();
    this.#parseVariantOptionMap();
    this.#bundleGroupId = this.#createBundleGroupId();
    this.refs.bundleGroupIdProperty.value = this.#bundleGroupId;

    this.#activeVariantId = this.#normalizeVariantId(this.dataset.currentVariantId || this.#getVariantIdFromForm());
    this.#applyLayoutForVariant(this.#activeVariantId);

    this.#form = this.#getProductForm();
    this.#form?.addEventListener('submit', this.#onFormSubmitCapture, true);

    this.#syncFromExternalBundleApp();
    this.#syncBundlePlanState();
    this.#syncFromInput();
    this.#syncExternalBundleTotalPreview();
    void this.#primeFeeSyncSnapshot();
    this.#setModalOpen(false);
    document.addEventListener('change', this.#onDocumentBundleChange, true);
    document.documentElement.addEventListener('cart:change', this.#onCartChange);

    this.#eventTarget = this.closest('.shopify-section, dialog, product-card') || document;
    this.#eventTarget.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#eventTarget?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#form?.removeEventListener('submit', this.#onFormSubmitCapture, true);
    document.removeEventListener('keydown', this.#onDocumentKeydown);
    document.removeEventListener('change', this.#onDocumentBundleChange, true);
    document.documentElement.removeEventListener('cart:change', this.#onCartChange);
    window.removeEventListener('resize', this.#onWindowResize);
    this.#resetExternalBundleTotalPreview();
    this.#resetModalViewportNormalization();
    document.body.classList.remove(BODY_MODAL_OPEN_CLASS);
    if (document.body.style.overflow === 'hidden') {
      document.body.style.overflow = this.#previousBodyOverflow;
    }
  }

  handleInput() {
    this.#activePreviewSlot = 1;
    this.#syncFromInput();
  }

  /**
   * @param {Event} [event]
   */
  handleBundleSlotInput(event) {
    const input = event?.target;
    if (input instanceof HTMLInputElement) {
      this.#activePreviewSlot = this.#toNumber(input.dataset.slotInputIndex, 1);
      this.#applyBundleSlotPreview(this.#activePreviewSlot);
    }
    this.#syncFromInput();
  }

  handlePlanChange() {
    if (this.#useExternalBundleApp) return;

    const selectedPlanQty = this.#getSelectedPlanQuantity();
    this.#activePreviewSlot = selectedPlanQty > 1
      ? Math.min(this.#activePreviewSlot, selectedPlanQty)
      : 1;

    this.#applyActiveVariantToUntouchedSlots();
    this.#syncBundlePlanState();
    this.#applyBundleSlotPreview(this.#activePreviewSlot);
    this.#syncFromInput();
  }

  handlePrimarySlotFocus() {
    this.#activePreviewSlot = 1;
    this.#applyBundleSlotPreview(1);
    this.#syncFromInput();
  }

  /**
   * @param {Event} event
   */
  handleBundleSlotFocus(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const slot = this.#toNumber(input.dataset.slotInputIndex, 1);
    this.#activePreviewSlot = slot;
    this.#applyBundleSlotPreview(slot);
    this.#syncFromInput();
  }

  /**
 * @param {Event} event
 */
handleBundleSlotVariantChange(event) {
  const select = event.target;
  if (!(select instanceof HTMLSelectElement)) return;

  const slot = this.#toNumber(select.dataset.slotVariantIndex, 1);
  const selectedOption = select.selectedOptions?.[0];

  const variantId = this.#normalizeVariantId(select.value);
  const color = (
    selectedOption?.dataset.color ||
    selectedOption?.textContent ||
    ''
  ).trim();

  this.#bundleSlotVariantIds[slot - 1] = variantId;
  this.#bundleSlotColors[slot - 1] = color;
  this.#activePreviewSlot = slot;
  this.#touchedSwatchSlots.add(slot);

  this.#syncBundlePlanState();
  this.#syncSwatchUI();
  this.#applyBundleSlotPreview(slot);
  this.#syncFromInput();
}

  /**
   * @param {Event} event
   */
  handleSlotTabClick(event) {
    event.preventDefault();

    const target = event.target;
    if (!(target instanceof Element)) return;

    const tabButton = target.closest('[data-slot-tab-index]');
    if (!(tabButton instanceof HTMLButtonElement)) return;

    const selectedPlanQty = this.#getSelectedPlanQuantity();
    const slot = this.#toNumber(tabButton.dataset.slotTabIndex, 1);
    if (slot < 1 || slot > selectedPlanQty) return;

    this.#activePreviewSlot = slot;
    this.#syncBundlePlanState();
    this.#applyBundleSlotPreview(slot);
    this.#syncFromInput();

    const slotInput = this.#getSlotInput(slot);
    if (slotInput instanceof HTMLInputElement) {
      slotInput.focus({ preventScroll: true });
    }
  }

  /**
   * @param {Event} event
   */
  handleSlotSwatchClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('[data-slot-swatch]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const slot = this.#toNumber(button.dataset.swatchSlotIndex, 1);
    const variantId = this.#normalizeVariantId(button.dataset.variantId || '');
    if (!variantId) return;

    const select = this.querySelector(`[data-slot-variant-index="${slot}"]`);
    if (select instanceof HTMLSelectElement) {
      select.value = variantId;
    }

    this.#bundleSlotVariantIds[slot - 1] = variantId;
    this.#bundleSlotColors[slot - 1] = (button.dataset.colorName || '').trim();
    this.#touchedSwatchSlots.add(slot);

    this.#syncSwatchUI();
    this.#activePreviewSlot = slot;
    this.#applyBundleSlotPreview(slot);
    this.#syncFromInput();
  }

  /** Mirrors the hidden slot variant selects onto the swatch buttons. */
  #syncSwatchUI() {
    const groups = Array.from(this.querySelectorAll('[data-slot-swatches-index]'));

    for (const group of groups) {
      const slot = this.#toNumber(group.getAttribute('data-slot-swatches-index'), 1);
      const select = this.querySelector(`[data-slot-variant-index="${slot}"]`);
      const currentId = this.#normalizeVariantId(
        (select instanceof HTMLSelectElement && select.value) ||
        this.#bundleSlotVariantIds[slot - 1] ||
        this.#activeVariantId ||
        ''
      );

      let currentName = '';
      for (const swatch of group.querySelectorAll('[data-slot-swatch]')) {
        if (!(swatch instanceof HTMLElement)) continue;
        const isSelected = this.#normalizeVariantId(swatch.dataset.variantId || '') === currentId;
        swatch.classList.toggle('is-selected', isSelected);
        if (isSelected) {
          currentName = swatch.dataset.colorName || '';
        }
      }

      const nameLabel = this.querySelector(`[data-swatch-name-index="${slot}"]`);
      if (nameLabel instanceof HTMLElement) {
        nameLabel.textContent = currentName;
      }
    }
  }

  /** Untouched slots default to the variant currently selected on the product page. */
  #applyActiveVariantToUntouchedSlots() {
    const activeId = this.#normalizeVariantId(this.#activeVariantId || this.#getVariantIdFromForm() || '');
    if (!activeId) return;

    const selects = Array.isArray(this.refs.bundleSlotVariantSelects) ? this.refs.bundleSlotVariantSelects : [];
    for (const select of selects) {
      if (!(select instanceof HTMLSelectElement)) continue;
      const slot = this.#toNumber(select.dataset.slotVariantIndex, 1);
      if (this.#touchedSwatchSlots.has(slot)) continue;
      const hasOption = Array.from(select.options).some((option) => option.value === activeId);
      if (hasOption) {
        select.value = activeId;
      }
    }
  }

  openModal() {
    this.#refreshActiveVariantFromForm();
    this.#applyActiveVariantToUntouchedSlots();
    this.#syncFromExternalBundleApp();
    this.#syncInternalVariantSelectors();
    this.#syncSwatchUI();
    this.#applyBundleSlotPreview(this.#activePreviewSlot);
    this.#draftState = this.#captureDraftState();
    this.#setModalOpen(true);
    this.#updatePreviewTextScaling();
  }

  saveModal(event) {
  event?.preventDefault();

  if (!this.#validateModal()) return;

  this.#draftState = this.#captureDraftState();
  this.#syncInternalVariantSelectors();
  this.#syncFromInput();

  const selectedPlanQty = this.#getSelectedPlanQuantity();
  const firstValue = this.refs.textInput?.value?.trim() || '';

  if (selectedPlanQty <= 1 && firstValue.length === 0) {
    this.#setModalOpen(false);
    return;
  }

  if (!this.#form) {
    this.#showInlineError('Produktformular konnte nicht gefunden werden.');
    return;
  }

  this.#setModalButtonLoading(true);

  if (selectedPlanQty > 1) {
    void this.#submitBundleItems(selectedPlanQty).finally(() => {
      this.#setModalButtonLoading(false);
    });
    return;
  }

  void this.#submitSinglePersonalizedItem(firstValue).finally(() => {
    this.#setModalButtonLoading(false);
  });
}

  /**
   * @param {Event} [event]
   */
  cancelModal(event) {
    event?.preventDefault();

    if (this.#draftState) {
      this.#applyDraftState(this.#draftState);
    }

    this.#setModalOpen(false);
  }

  /**
   * @param {Event} event
   */
  handleModalBackdropClick(event) {
    const modal = this.refs.modal;
    const eventPath = typeof event.composedPath === 'function' ? event.composedPath() : [];
    const source = eventPath.length > 0 ? eventPath[0] : event.target;

    if (source !== modal) return;
    this.cancelModal(event);
  }

  /**
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    const customEvent = /** @type {CustomEvent | undefined} */ (event);
    const variantId = customEvent?.detail?.resource?.id;

    if (!variantId) return;

    this.#activeVariantId = this.#normalizeVariantId(variantId);
    this.#applyLayoutForVariant(this.#activeVariantId);
    this.#syncFromInput();
  };

  /**
   * @param {Event} event
   */
  #onFormSubmitCapture = (event) => {
    const selectedPlanQty = this.#getSelectedPlanQuantity();
    const primaryInput = this.refs.textInput;
    const rawValue = primaryInput?.value || '';
    const trimmedValue = rawValue.trim();
    const isSinglePersonalized = selectedPlanQty <= 1 && trimmedValue.length > 0;

    if (this.#useExternalBundleApp) {
      if (selectedPlanQty <= 1 && !isSinglePersonalized) return;
      event.preventDefault();
      event.stopImmediatePropagation();

      if (!this.#isSubmittingBundle) {
        void this.#submitExternalBundleItems(selectedPlanQty);
      }
      return;
    }

    if (selectedPlanQty <= 1) {
      if (!isSinglePersonalized) return;

      if (!(primaryInput instanceof HTMLInputElement)) return;

      if (!this.#hasValidCharacters(rawValue)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        primaryInput.setCustomValidity(
          primaryInput.dataset.invalidCharsMessage || 'Erlaubt sind Buchstaben, Zahlen, Leerzeichen und . - & +'
        );
        primaryInput.reportValidity();
        return;
      }

      primaryInput.setCustomValidity('');

      if (!this.#hasEngravingFeeConfigured()) {
        event.preventDefault();
        event.stopImmediatePropagation();
        this.#showInlineError(this.#getEngravingFeeMissingMessage());
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (!this.#isSubmittingBundle) {
        void this.#submitSinglePersonalizedItem(trimmedValue);
      }
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (!this.#isSubmittingBundle) {
      void this.#submitBundleItems(selectedPlanQty);
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #onDocumentKeydown = (event) => {
    if (!this.#isModalOpen) return;
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.cancelModal();
  };

  /**
   * @param {Event} event
   */
  #onDocumentBundleChange = (event) => {
    if (!this.#useExternalBundleApp && !this.#externalBundleRoot) {
      this.#syncFromExternalBundleApp();
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!this.#externalBundleRoot?.contains(target)) return;

    this.#syncFromExternalBundleApp();
    this.#syncFromInput();
  };

  #onWindowResize = () => {
    if (!this.#isModalOpen) return;
    this.#normalizeModalToViewport();
  };

  #syncFromInput() {
    const selectedPlanQty = this.#getSelectedPlanQuantity();
    const isBundle = selectedPlanQty > 1;
    const currentPreviewSlot = isBundle
      ? Math.max(1, Math.min(selectedPlanQty, this.#activePreviewSlot))
      : 1;
    this.#activePreviewSlot = currentPreviewSlot;
    const previewInput = this.#getSlotInput(currentPreviewSlot) || this.refs.textInput;

    const rawValue = previewInput.value;
    const trimmedValue = rawValue.trim();

    this.#syncSlotCounters();

    const placeholder = this.dataset.previewPlaceholder || '';
    const previewValue = trimmedValue.length > 0 ? trimmedValue : placeholder;
    this.refs.previewText.textContent = previewValue;
    this.#updatePreviewTextScaling();

    const hasValidCharacters = this.#hasValidCharacters(rawValue);

    if (!hasValidCharacters) {
      previewInput.setCustomValidity(
        previewInput.dataset.invalidCharsMessage ||
        'Erlaubt sind Buchstaben, Zahlen, Leerzeichen und . - & +'
      );
    } else {
      previewInput.setCustomValidity('');
    }

    if (selectedPlanQty > 1) {
      if (this.#useExternalBundleApp) {
        this.#prepareExternalBundleProperties(selectedPlanQty, false);
      } else {
        clearEngravingCartInjection();
        this.#clearPrimaryProperties();
        this.#setPropertyInputsEnabled(false);
        this.#clearBundleSlotProperties();
      }
      this.#updateLauncherSummary();
      if (!this.#isModalOpen) {
        this.#syncExternalBundleTotalPreview();
      }
      return;
    }

    const isPersonalized = trimmedValue.length > 0 && hasValidCharacters;

    if (!isPersonalized) {
      clearEngravingCartInjection();
      this.#clearPrimaryProperties();
      this.#setPropertyInputsEnabled(false);
      this.#clearBundleSlotProperties();
      this.#updateLauncherSummary();
      if (!this.#isModalOpen) {
        this.#syncExternalBundleTotalPreview();
      }
      return;
    }

    this.refs.visibleProperty.value = trimmedValue;
    this.refs.engravingTextProperty.value = trimmedValue;
    this.refs.personalizedProperty.value = '1';
    this.refs.engravingSlotProperty.value = '1';
    this.refs.bundleGroupIdProperty.value = this.#bundleGroupId;
    this.refs.bundlePlanProperty.value = 'single';

    clearEngravingCartInjection();
    this.#setPropertyInputsEnabled(true);
    this.#clearBundleSlotProperties();
    this.#updateLauncherSummary();
    if (!this.#isModalOpen) {
      this.#syncExternalBundleTotalPreview();
    }
  }

  /**
   * @returns {number | null}
   */
  #getEngravingFeeVariantId() {
    const rawValue = (this.dataset.engravingFeeVariantId || '').trim();
    const normalizedVariantId = normalizeVariantId(rawValue);
    if (!/^\d+$/.test(normalizedVariantId)) return null;
    return Number(normalizedVariantId);
  }

  /**
   * @returns {string}
   */
  #getEngravingFeePrice() {
    return (this.dataset.engravingFeePrice || '').trim();
  }

  /**
   * @returns {number}
   */
  #getEngravingFeeUnitCents() {
    const fromCentsAttribute = this.#toNumber(this.dataset.engravingFeePriceCents, 0);
    if (fromCentsAttribute > 0) {
      return Math.round(fromCentsAttribute);
    }

    const parsedFallback = this.#parsePriceText(this.#getEngravingFeePrice());
    return parsedFallback ? Math.max(0, parsedFallback.cents) : 0;
  }

  /**
   * @returns {string}
   */
  #getEngravingFeeMissingMessage() {
    return (this.dataset.engravingFeeMissingMessage || '').trim() || 'Gravur-Aufpreis ist noch nicht konfiguriert.';
  }

  /**
   * @returns {boolean}
   */
  #hasEngravingFeeConfigured() {
    return this.#getEngravingFeeVariantId() !== null;
  }

  #syncExternalBundleTotalPreview() {
    if (!this.#useExternalBundleApp) {
      this.#resetExternalBundleTotalPreview();
      return;
    }

    const externalRoot = this.#externalBundleRoot;
    if (!(externalRoot instanceof HTMLElement)) {
      this.#resetExternalBundleTotalPreview();
      return;
    }

    const totalNode = this.#findExternalBundleTotalNode(externalRoot);
    if (!(totalNode instanceof HTMLElement)) {
      this.#resetExternalBundleTotalPreview();
      return;
    }

    const totalValueNode = this.#findExternalBundleTotalValueNode(totalNode);
    if (!(totalValueNode instanceof HTMLElement)) {
      this.#resetExternalBundleTotalPreview();
      return;
    }

    if (this.#externalBundleTotalNode !== totalNode || this.#externalBundleTotalValueNode !== totalValueNode) {
      this.#resetExternalBundleTotalPreview();
      this.#externalBundleTotalNode = totalNode;
      this.#externalBundleTotalValueNode = totalValueNode;
      this.#externalBundleTotalObserver = new MutationObserver(() => {
        if (this.#isWritingExternalBundleTotal) return;
        this.#captureExternalBundleTotalBase();
        this.#renderExternalBundleTotalPreview();
      });
      this.#externalBundleTotalObserver.observe(totalNode, {
        subtree: true,
        childList: true,
        characterData: true,
      });
    }

    this.#captureExternalBundleTotalBase();
    this.#renderExternalBundleTotalPreview();
  }

  #resetExternalBundleTotalPreview() {
    if (this.#externalBundleTotalValueNode && this.#externalBundleTotalBaseText) {
      const currentText = this.#normalizeText(this.#externalBundleTotalValueNode.textContent || '');
      if (currentText === this.#externalBundleTotalLastRenderedText) {
        this.#isWritingExternalBundleTotal = true;
        this.#externalBundleTotalValueNode.textContent = this.#externalBundleTotalBaseText;
        this.#isWritingExternalBundleTotal = false;
      }
    }

    this.#externalBundleTotalObserver?.disconnect();
    this.#externalBundleTotalObserver = null;
    this.#externalBundleTotalNode = null;
    this.#externalBundleTotalValueNode = null;
    this.#externalBundleTotalBaseText = '';
    this.#externalBundleTotalLastRenderedText = '';
    this.#externalBundleTotalTemplate = null;
  }

  /**
   * @param {HTMLElement} externalRoot
   * @returns {HTMLElement | null}
   */
  #findExternalBundleTotalNode(externalRoot) {
    const scopes = [
      externalRoot,
      externalRoot.parentElement,
      this.#form?.closest('product-form-component'),
      this.closest('.shopify-section'),
    ];
    const visited = new Set();
    const candidates = [];

    for (const scope of scopes) {
      if (!(scope instanceof HTMLElement) && !(scope instanceof Document)) continue;

      const elements = scope.querySelectorAll('p,div,span,strong,b');
      for (const element of elements) {
        if (!(element instanceof HTMLElement)) continue;
        if (visited.has(element)) continue;
        visited.add(element);

        if (element.closest('engraving-personalizer-component') === this) continue;

        const text = this.#normalizeText(element.textContent || '');
        if (!text) continue;
        if (!/\b(gesamt|total)\b/i.test(text)) continue;
        if (/(gesamtersparnisse|total savings|sparnis)/i.test(text)) continue;
        if (!this.#containsPriceToken(text)) continue;

        let score = 0;
        if (externalRoot.contains(element)) score += 30;
        if (element.tagName === 'P' || element.tagName === 'DIV') score += 10;
        if (/:\s*[^\n]*\d/.test(text)) score += 10;
        if (text.length <= 45) score += 8;
        if (text.length > 90) score -= 15;

        candidates.push({ element, score });
      }
    }

    if (candidates.length === 0) return null;

    candidates.sort((a, b) => b.score - a.score);
    return candidates[0].element;
  }

  /**
   * @param {HTMLElement} totalNode
   * @returns {HTMLElement | null}
   */
  #findExternalBundleTotalValueNode(totalNode) {
    const leafCandidates = [];
    const leafElements = totalNode.querySelectorAll('*');

    for (const element of leafElements) {
      if (!(element instanceof HTMLElement)) continue;
      if (element.children.length > 0) continue;

      const text = this.#normalizeText(element.textContent || '');
      if (!text) continue;
      if (!this.#containsPriceToken(text)) continue;

      let score = 0;
      if (/(gesamt|total)/i.test(text)) {
        score -= 20;
      } else {
        score += 20;
      }

      if (text.length <= 24) score += 10;
      if (text.length > 60) score -= 10;

      leafCandidates.push({ element, score });
    }

    if (leafCandidates.length > 0) {
      leafCandidates.sort((a, b) => b.score - a.score);
      return leafCandidates[0].element;
    }

    const nodeText = this.#normalizeText(totalNode.textContent || '');
    if (nodeText && this.#containsPriceToken(nodeText)) {
      return totalNode;
    }

    return null;
  }

  #captureExternalBundleTotalBase() {
    if (!(this.#externalBundleTotalValueNode instanceof HTMLElement)) return;

    const currentText = this.#normalizeText(this.#externalBundleTotalValueNode.textContent || '');
    if (!currentText) return;
    if (currentText === this.#externalBundleTotalLastRenderedText) return;

    const parsed = this.#parsePriceText(currentText);
    if (!parsed) return;

    this.#externalBundleTotalBaseText = currentText;
    this.#externalBundleTotalTemplate = parsed;
  }

  #renderExternalBundleTotalPreview() {
    if (!(this.#externalBundleTotalValueNode instanceof HTMLElement)) return;
    if (!this.#externalBundleTotalTemplate) return;

    const feeUnitCents = this.#getEngravingFeeUnitCents();
    const personalizedCount = this.#countPersonalizedSlotsForFee();

    if (feeUnitCents <= 0 || personalizedCount <= 0) {
      if (this.#externalBundleTotalBaseText) {
        const currentText = this.#normalizeText(this.#externalBundleTotalValueNode.textContent || '');
        if (currentText !== this.#externalBundleTotalBaseText) {
          this.#isWritingExternalBundleTotal = true;
          this.#externalBundleTotalValueNode.textContent = this.#externalBundleTotalBaseText;
          this.#isWritingExternalBundleTotal = false;
        }
      }
      this.#externalBundleTotalLastRenderedText = '';
      return;
    }

    const totalCents = this.#externalBundleTotalTemplate.cents + feeUnitCents * personalizedCount;
    const numberText = this.#formatPriceFromTemplate(totalCents, this.#externalBundleTotalTemplate);
    const renderedText = `${this.#externalBundleTotalTemplate.prefix}${numberText}${this.#externalBundleTotalTemplate.suffix}`;
    const currentText = this.#normalizeText(this.#externalBundleTotalValueNode.textContent || '');

    if (currentText === renderedText) {
      this.#externalBundleTotalLastRenderedText = renderedText;
      return;
    }

    this.#isWritingExternalBundleTotal = true;
    this.#externalBundleTotalValueNode.textContent = renderedText;
    this.#isWritingExternalBundleTotal = false;
    this.#externalBundleTotalLastRenderedText = renderedText;
  }

  /**
   * @returns {number}
   */
  #countPersonalizedSlotsForFee() {
    const selectedPlanQty = this.#getSelectedPlanQuantity();
    let count = 0;

    for (let slot = 1; slot <= selectedPlanQty; slot += 1) {
      const input = this.#getSlotInput(slot);
      if (!(input instanceof HTMLInputElement)) continue;

      const rawValue = input.value || '';
      const trimmedValue = rawValue.trim();
      if (trimmedValue.length === 0) continue;
      if (!this.#hasValidCharacters(rawValue)) continue;
      count += 1;
    }

    return count;
  }

  /**
   * @param {string} text
   * @returns {boolean}
   */
  #containsPriceToken(text) {
    return /-?\d{1,3}(?:[ '\u00A0.,]\d{3})*(?:[.,]\d+)?|-?\d+(?:[.,]\d+)?/.test(text);
  }

  /**
   * @param {string} text
   * @returns {{ prefix: string, suffix: string, cents: number, decimals: number, decimalSeparator: string, groupSeparator: string } | null}
   */
  #parsePriceText(text) {
    const normalizedText = this.#normalizeText(text);
    if (!normalizedText) return null;

    const numberMatches = normalizedText.match(/-?\d{1,3}(?:[ '\u00A0.,]\d{3})*(?:[.,]\d+)?|-?\d+(?:[.,]\d+)?/g);
    if (!numberMatches || numberMatches.length === 0) return null;

    const numberToken = numberMatches[numberMatches.length - 1];
    const tokenIndex = normalizedText.lastIndexOf(numberToken);
    if (tokenIndex < 0) return null;

    const parsedToken = this.#parsePriceToken(numberToken);
    if (!parsedToken) return null;

    return {
      prefix: normalizedText.slice(0, tokenIndex),
      suffix: normalizedText.slice(tokenIndex + numberToken.length),
      cents: parsedToken.cents,
      decimals: parsedToken.decimals,
      decimalSeparator: parsedToken.decimalSeparator,
      groupSeparator: parsedToken.groupSeparator,
    };
  }

  /**
   * @param {string} token
   * @returns {{ cents: number, decimals: number, decimalSeparator: string, groupSeparator: string } | null}
   */
  #parsePriceToken(token) {
    const sanitized = (token || '').trim();
    if (!sanitized) return null;

    const sign = sanitized.startsWith('-') ? -1 : 1;
    const unsigned = sanitized.replace(/^[+-]/, '');
    const lastDot = unsigned.lastIndexOf('.');
    const lastComma = unsigned.lastIndexOf(',');
    let decimalIndex = Math.max(lastDot, lastComma);
    let decimalSeparator = '';

    if (decimalIndex >= 0) {
      const decimalTail = unsigned.slice(decimalIndex + 1).replace(/[^\d]/g, '');
      if (decimalTail.length === 0 || decimalTail.length > 3) {
        decimalIndex = -1;
      } else {
        decimalSeparator = unsigned[decimalIndex];
      }
    }

    const integerPartRaw = decimalIndex >= 0 ? unsigned.slice(0, decimalIndex) : unsigned;
    const decimalPartRaw = decimalIndex >= 0 ? unsigned.slice(decimalIndex + 1) : '';
    const integerDigits = integerPartRaw.replace(/[^\d]/g, '');
    const decimalDigits = decimalPartRaw.replace(/[^\d]/g, '');

    if (!integerDigits && !decimalDigits) return null;

    const numericString = `${integerDigits || '0'}${decimalDigits ? `.${decimalDigits}` : ''}`;
    const numericValue = Number(numericString);
    if (!Number.isFinite(numericValue)) return null;

    let groupSeparator = '';
    if (integerPartRaw.includes("'")) {
      groupSeparator = "'";
    } else if (/[ \u00A0]/.test(integerPartRaw)) {
      groupSeparator = ' ';
    } else if (integerPartRaw.includes(',') && decimalSeparator !== ',') {
      groupSeparator = ',';
    } else if (integerPartRaw.includes('.') && decimalSeparator !== '.') {
      groupSeparator = '.';
    }

    return {
      cents: Math.round(numericValue * 100) * sign,
      decimals: decimalSeparator ? Math.min(2, Math.max(1, decimalDigits.length)) : 0,
      decimalSeparator: decimalSeparator || '.',
      groupSeparator,
    };
  }

  /**
   * @param {number} cents
   * @param {{ decimals: number, decimalSeparator: string, groupSeparator: string }} template
   * @returns {string}
   */
  #formatPriceFromTemplate(cents, template) {
    const sign = cents < 0 ? '-' : '';
    const absoluteValue = Math.abs(cents) / 100;
    const decimals = Math.max(0, Math.min(2, this.#toNumber(template.decimals, 2)));
    const decimalSeparator = template.decimalSeparator || '.';
    const groupSeparator = template.groupSeparator || '';

    if (decimals === 0) {
      const rounded = Math.round(absoluteValue).toString();
      const grouped = groupSeparator ? rounded.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator) : rounded;
      return `${sign}${grouped}`;
    }

    const [integerPart, decimalPart] = absoluteValue.toFixed(decimals).split('.');
    const groupedInteger = groupSeparator
      ? integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, groupSeparator)
      : integerPart;

    return `${sign}${groupedInteger}${decimalSeparator}${decimalPart}`;
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  #normalizeText(text) {
    return (text || '')
      .toString()
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * @param {number} quantity
   * @param {string} engravingGroupId
   * @returns {{id: number, quantity: number, properties: Record<string, string>} | null}
   */
  #buildEngravingFeeItem(quantity, engravingGroupId) {
    const feeVariantId = this.#getEngravingFeeVariantId();
    if (!feeVariantId || quantity <= 0) return null;

    return {
      id: feeVariantId,
      quantity,
      properties: {
        _engraving_fee: '1',
        _engraving_group_id: engravingGroupId,
      },
    };
  }

  /**
   * @returns {Promise<any>}
   */
  async #fetchCartJson() {
    const root = (window.Shopify?.routes?.root || '/').toString();
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    const response = await fetch(`${normalizedRoot}cart.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  }

  async #syncEngravingFees() {
    if (!this.#hasEngravingFeeConfigured()) return;

    this.#isSyncingEngravingFees = true;

    try {
      let cart = await this.#fetchCartJson();
      const feeVariantId = this.#getEngravingFeeVariantId();

      if (!feeVariantId || !Array.isArray(cart?.items)) return;
      this.#trackFeeSuppression(cart.items);

      const desiredQuantities = new Map();
      const feeGroups = new Map();

      for (const item of cart.items) {
        const properties = item.properties || {};
        const engravingGroupId = (properties._engraving_group_id || '').toString().trim();
        const isFeeLine = String(properties._engraving_fee || '') === '1';

        if (item.variant_id === feeVariantId && isFeeLine) {
          const existingFeeLines = feeGroups.get(engravingGroupId) || [];
          existingFeeLines.push(item);
          feeGroups.set(engravingGroupId, existingFeeLines);
          continue;
        }

        const isPersonalized = String(properties._personalized || '') === '1' || Boolean(properties._engraving_text);
        if (!engravingGroupId || !isPersonalized) continue;

        desiredQuantities.set(
          engravingGroupId,
          (desiredQuantities.get(engravingGroupId) || 0) + Number(item.quantity || 0)
        );
      }

      const allGroupIds = new Set([...desiredQuantities.keys(), ...feeGroups.keys()]);

      for (const engravingGroupId of allGroupIds) {
        const desiredQuantity = desiredQuantities.get(engravingGroupId) || 0;
        const existingFeeLines = feeGroups.get(engravingGroupId) || [];

        if (desiredQuantity === 0) {
          this.#suppressedFeeGroupIds.delete(engravingGroupId);

          for (const feeLine of existingFeeLines) {
            await fetch(
              this.#getCartChangeUrl(),
              fetchConfig('json', {
                body: JSON.stringify({ id: feeLine.key, quantity: 0 }),
              })
            );
          }
          continue;
        }

        if (existingFeeLines.length === 0) {
          if (this.#suppressedFeeGroupIds.has(engravingGroupId)) {
            continue;
          }

          const feeItem = this.#buildEngravingFeeItem(desiredQuantity, engravingGroupId);
          if (feeItem) {
            await fetch(
              this.#getCartAddUrl(),
              fetchConfig('json', {
                body: JSON.stringify({ items: [feeItem] }),
              })
            );
          }
          continue;
        }

        const [primaryFeeLine, ...extraFeeLines] = existingFeeLines;
        this.#suppressedFeeGroupIds.delete(engravingGroupId);

        if (Number(primaryFeeLine.quantity || 0) !== desiredQuantity) {
          await fetch(
            this.#getCartChangeUrl(),
            fetchConfig('json', {
              body: JSON.stringify({ id: primaryFeeLine.key, quantity: desiredQuantity }),
            })
          );
        }

        for (const extraFeeLine of extraFeeLines) {
          await fetch(
            this.#getCartChangeUrl(),
            fetchConfig('json', {
              body: JSON.stringify({ id: extraFeeLine.key, quantity: 0 }),
            })
          );
        }
      }

      // Trigger a full cart UI refresh so fee-line removals appear immediately
      // in drawer/page without requiring a manual page reload.
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } catch (error) {
      console.warn('Failed to sync engraving fees', error);
    } finally {
      this.#isSyncingEngravingFees = false;
    }
  }

  async #primeFeeSyncSnapshot() {
    if (!this.#hasEngravingFeeConfigured()) return;

    try {
      const cart = await this.#fetchCartJson();
      if (Array.isArray(cart?.items)) {
        this.#trackFeeSuppression(cart.items);
      }
    } catch (error) {
      console.warn('Failed to prime engraving fee snapshot', error);
    }
  }

  /**
   * @param {Array<any>} cartItems
   */
  #trackFeeSuppression(cartItems) {
    const feeVariantId = this.#getEngravingFeeVariantId();
    if (!feeVariantId || !Array.isArray(cartItems)) return;

    const currentSnapshot = this.#buildEngravingGroupSnapshot(cartItems, feeVariantId);

    if (this.#lastEngravingGroupSnapshot.size > 0) {
      const allGroupIds = new Set([
        ...this.#lastEngravingGroupSnapshot.keys(),
        ...currentSnapshot.keys(),
      ]);

      for (const groupId of allGroupIds) {
        const previous = this.#lastEngravingGroupSnapshot.get(groupId) || { personalizedQty: 0, feeQty: 0 };
        const current = currentSnapshot.get(groupId) || { personalizedQty: 0, feeQty: 0 };

        const removedFeeLineManually = previous.feeQty > 0 && current.feeQty === 0 && current.personalizedQty > 0;
        if (removedFeeLineManually) {
          this.#suppressedFeeGroupIds.add(groupId);
        }

        if (current.personalizedQty === 0 || current.feeQty > 0) {
          this.#suppressedFeeGroupIds.delete(groupId);
        }
      }
    } else {
      for (const groupId of Array.from(this.#suppressedFeeGroupIds)) {
        const current = currentSnapshot.get(groupId);
        if (!current || current.personalizedQty === 0 || current.feeQty > 0) {
          this.#suppressedFeeGroupIds.delete(groupId);
        }
      }
    }

    this.#lastEngravingGroupSnapshot = currentSnapshot;
  }

  /**
   * @param {Array<any>} cartItems
   * @param {number} feeVariantId
   * @returns {Map<string, { personalizedQty: number, feeQty: number }>}
   */
  #buildEngravingGroupSnapshot(cartItems, feeVariantId) {
    const snapshot = new Map();

    for (const item of cartItems) {
      const properties = item?.properties || {};
      const engravingGroupId = (properties._engraving_group_id || '').toString().trim();
      if (!engravingGroupId) continue;

      const current = snapshot.get(engravingGroupId) || { personalizedQty: 0, feeQty: 0 };
      const quantity = Number(item?.quantity || 0);
      const isFeeLine = item?.variant_id === feeVariantId && String(properties._engraving_fee || '') === '1';

      if (isFeeLine) {
        current.feeQty += quantity;
      } else {
        const isPersonalized = String(properties._personalized || '') === '1' || Boolean(properties._engraving_text);
        if (isPersonalized) {
          current.personalizedQty += quantity;
        }
      }

      snapshot.set(engravingGroupId, current);
    }

    return snapshot;
  }

  /**
 * @param {string} engravingText
 */
async #submitSinglePersonalizedItem(engravingText) {
  if (!this.#form) return;

  const variantId = this.#getVariantIdForSlot(1);
  if (!variantId) return;

  const feeItem = this.#buildEngravingFeeItem(1, this.#bundleGroupId);
  if (!feeItem) {
    this.#showInlineError(this.#getEngravingFeeMissingMessage());
    return;
  }

  const visiblePropertyKey = this.#getVisiblePropertyKey();
  const color = this.#resolveColorForSlot(1);
  const configId = this.#resolveConfigIdForSlot(1);
  const previewVersion = this.refs.previewVersionProperty.value || 'v1';
  const sectionIds = this.#getCartBundledSectionIds();
  const sectionsUrl = this.#getSectionsUrlForVariant(variantId);
  const addToCartButton = this.#form.querySelector('[ref="addToCartButton"]');

  /** @type {{items: Array<any>, sections?: string, sections_url?: string}} */
  const payload = {
    items: [
      {
        id: Number(variantId),
        quantity: 1,
        properties: {
          [visiblePropertyKey]: engravingText,
          _personalized: '1',
          _engraving_text: engravingText,
          _engraving_slot: '1',
          _engraving_color: color,
          _engraving_config_id: configId,
          _bundle_group_id: this.#bundleGroupId,
          _engraving_group_id: this.#bundleGroupId,
          _bundle_plan: 'single',
          _preview_version: previewVersion,
          _engraving_text_1: engravingText,
          _engraving_color_1: color,
          _engraving_config_id_1: configId,
        },
      },
      feeItem,
    ],
  };

  if (sectionIds.length > 0) {
    payload.sections = sectionIds.join(',');
  }

  if (sectionsUrl) {
    payload.sections_url = sectionsUrl;
  }

  this.#isSubmittingBundle = true;
  this.#clearInlineError();

  if (addToCartButton instanceof HTMLButtonElement) {
    addToCartButton.disabled = true;
  }

  try {
    const response = await fetch(
      this.#getCartAddUrl(),
      fetchConfig('json', {
        body: JSON.stringify(payload),
      })
    );

    const responseJson = await response.json();

    if (responseJson.status) {
      this.#showInlineError(responseJson.message || 'Personalisierte Flasche konnte nicht hinzugefügt werden.');
      return;
    }

    this.dispatchEvent(
      new CartAddEvent({}, variantId, {
        source: 'product-form-component',
        itemCount: 2,
        productId: this.#form.closest('product-form-component')?.dataset.productId,
        sections: responseJson.sections,
      })
    );

    await this.#dispatchThemeCartChange(responseJson, variantId, sectionIds);

    this.#setModalOpen(false);
    this.#resetPersonalizerAfterAddToCart();
    this.#clearInlineError();
  } catch (error) {
    console.error(error);
    this.#showInlineError('Personalisierte Flasche konnte nicht hinzugefügt werden. Bitte erneut versuchen.');
    this.#dispatchThemeCartError('Personalisierte Flasche konnte nicht hinzugefügt werden. Bitte erneut versuchen.');
  } finally {
    this.#isSubmittingBundle = false;

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = false;
    }
  }
}

  #syncBundlePlanState() {
    const selectedPlanQty = this.#getSelectedPlanQuantity();
    const isBundle = selectedPlanQty > 1;
    const activeSlot = isBundle
      ? Math.max(1, Math.min(selectedPlanQty, this.#activePreviewSlot))
      : 1;

    this.#activePreviewSlot = activeSlot;

    this.refs.bundlePlanProperty.value = isBundle ? `bundle_${selectedPlanQty}` : 'single';

    const singleLabel = this.dataset.slotSingleLabel || 'Name';
    const multiTemplate = this.dataset.slotMultiLabelTemplate || 'Name Flasche [index]';
    const editingLabel = isBundle ? multiTemplate.replace('[index]', '1') : singleLabel;
    this.refs.slotOneLabel.textContent = editingLabel;

    const slotTabsContainers = Array.from(this.querySelectorAll('[data-slot-tabs-container]'));
    for (const slotTabsContainer of slotTabsContainers) {
      slotTabsContainer.toggleAttribute('hidden', !isBundle);
    }

    const slotTabButtons = Array.from(this.querySelectorAll('[data-slot-tab-index]'));
    for (const tabButton of slotTabButtons) {
      const slotIndex = this.#toNumber(tabButton.getAttribute('data-slot-tab-index'), 0);
      const isSlotInPlan = slotIndex >= 1 && slotIndex <= selectedPlanQty;
      const isActive = isBundle && slotIndex === activeSlot;

      tabButton.toggleAttribute('hidden', !isBundle || !isSlotInPlan);
      tabButton.classList.toggle('is-active', isActive);
      tabButton.setAttribute('aria-pressed', String(isActive));

      const tabText = tabButton.querySelector('.engraving-personalizer__slot-tab-text');
      if (tabText instanceof HTMLElement) {
        tabText.textContent = this.#buildSlotTabLabel(slotIndex);
      }

      this.#syncSlotTabIcon(tabButton, slotIndex, isBundle && isSlotInPlan);
    }

    const slotContainers = Array.from(this.querySelectorAll('[data-slot-container][data-slot-index]'));
    let hasVisibleExtraSlot = false;
    for (const slotContainer of slotContainers) {
      const slotIndex = Number(slotContainer.getAttribute('data-slot-index'));
      const isSlotInPlan = slotIndex >= 1 && slotIndex <= selectedPlanQty;
      // All slots of the selected plan stay visible at once (stacked layout, no tabs).
      const shouldShow = isSlotInPlan;
      slotContainer.toggleAttribute('hidden', !shouldShow);
      if (shouldShow && slotIndex > 1) {
        hasVisibleExtraSlot = true;
      }

      const label = slotContainer.querySelector('.engraving-personalizer__slot-label');
      if (label instanceof HTMLElement) {
        label.textContent = isBundle
          ? multiTemplate.replace('[index]', String(slotIndex))
          : this.#buildSlotLabel(slotIndex, false, singleLabel, multiTemplate);
      }

      if (!isSlotInPlan) {
        const input = slotContainer.querySelector('input');
        if (input instanceof HTMLInputElement) {
          input.value = '';
          input.setCustomValidity('');
        }
      }
    }

    const slotList = this.querySelector('.engraving-personalizer__slot-list');
    if (slotList instanceof HTMLElement) {
      slotList.toggleAttribute('hidden', !hasVisibleExtraSlot);
    }

    const quantityInput = this.#form?.querySelector('input[name="quantity"]');
    if (quantityInput instanceof HTMLInputElement && isBundle && !this.#useExternalBundleApp) {
      quantityInput.value = '1';
    }

    // Dynamic checkout bypasses our custom multi-item payload; hide it for bundle plans.
    const acceleratedCheckout = this.#form?.closest('product-form-component')?.querySelector(
      '[ref="acceleratedCheckoutButtonContainer"]'
    );
    if (acceleratedCheckout instanceof HTMLElement) {
  acceleratedCheckout.toggleAttribute('hidden', isBundle);
}

this.#syncInternalVariantSelectors();
  }

  /**
 * @param {number} selectedPlanQty
 */
async #submitBundleItems(selectedPlanQty) {
  if (!this.#form) return;

  this.#syncInternalVariantSelectors();

  const engravings = this.#collectBundleEngravings(selectedPlanQty);
  if (!engravings) return;

  const personalizedCount = engravings.filter((value) => value.trim().length > 0).length;

  if (personalizedCount > 0 && !this.#hasEngravingFeeConfigured()) {
    this.#showInlineError(this.#getEngravingFeeMissingMessage());
    return;
  }

  const visiblePropertyKey = this.#getVisiblePropertyKey();
  const bundlePlan = `bundle_${selectedPlanQty}`;
  const previewVersion = this.refs.previewVersionProperty.value || 'v1';

  const items = [];

  for (let slot = 1; slot <= selectedPlanQty; slot += 1) {
    const engravingText = (engravings[slot - 1] || '').trim();
    const variantId = this.#getVariantIdForSlot(slot);

    if (!variantId || !/^\d+$/.test(variantId)) {
      this.#showInlineError(`Variante für Flasche ${slot} konnte nicht aufgelöst werden.`);
      return;
    }

    const color = this.#resolveColorForSlot(slot);
    const configId = this.#resolveConfigIdForSlot(slot);

    const item = {
      id: Number(variantId),
      quantity: 1,
    };

    if (engravingText) {
      item.properties = {
        [visiblePropertyKey]: engravingText,
        _personalized: '1',
        _engraving_text: engravingText,
        _engraving_slot: String(slot),
        _engraving_color: color,
        _engraving_config_id: configId,
        _bundle_group_id: this.#bundleGroupId,
        _engraving_group_id: this.#bundleGroupId,
        _bundle_plan: bundlePlan,
        _preview_version: previewVersion,
        [`_engraving_text_${slot}`]: engravingText,
        [`_engraving_color_${slot}`]: color,
        [`_engraving_config_id_${slot}`]: configId,
      };
    }

    items.push(item);
  }

  const feeItem = this.#buildEngravingFeeItem(personalizedCount, this.#bundleGroupId);
  if (feeItem) {
    items.push(feeItem);
  }

  const sectionIds = this.#getCartBundledSectionIds();

  /** @type {{items: Array<any>, sections?: string, sections_url?: string}} */
  const payload = { items };

  if (sectionIds.length > 0) {
    payload.sections = sectionIds.join(',');
  }

  const firstVariantId = this.#getVariantIdForSlot(1);
  const sectionsUrl = this.#getSectionsUrlForVariant(firstVariantId);

  if (sectionsUrl) {
    payload.sections_url = sectionsUrl;
  }

  const addToCartButton = this.#form.querySelector('[ref="addToCartButton"]');

  this.#isSubmittingBundle = true;
  this.#clearInlineError();

  if (addToCartButton instanceof HTMLButtonElement) {
    addToCartButton.disabled = true;
  }

  try {
    const response = await fetch(
      this.#getCartAddUrl(),
      fetchConfig('json', {
        body: JSON.stringify(payload),
      })
    );

    const responseJson = await response.json();

    if (responseJson.status) {
      this.#showInlineError(responseJson.message || 'Bundle konnte nicht hinzugefügt werden.');
      return;
    }

    this.dispatchEvent(
      new CartAddEvent({}, firstVariantId, {
        source: 'product-form-component',
        itemCount: items.length,
        productId: this.#form.closest('product-form-component')?.dataset.productId,
        sections: responseJson.sections,
      })
    );

    await this.#dispatchThemeCartChange(responseJson, firstVariantId, sectionIds);

    this.#setModalOpen(false);
    this.#resetPersonalizerAfterAddToCart();
    this.#clearInlineError();
  } catch (error) {
    console.error(error);
    this.#showInlineError('Bundle konnte nicht hinzugefügt werden. Bitte erneut versuchen.');
    this.#dispatchThemeCartError('Bundle konnte nicht hinzugefügt werden. Bitte erneut versuchen.');
  } finally {
    this.#isSubmittingBundle = false;

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = false;
    }
  }
}

  /**
   * @param {number} selectedPlanQty
   */
  async #submitExternalBundleItems(selectedPlanQty) {
    if (!this.#form) return;

    this.#refreshActiveVariantFromForm();
    this.#syncFromExternalBundleApp();

    const engravings = [];
    for (let slot = 1; slot <= selectedPlanQty; slot += 1) {
      const input = this.#getSlotInput(slot);
      if (!(input instanceof HTMLInputElement)) {
        this.#showInlineError('Bundle-Namefelder konnten nicht gelesen werden.');
        return;
      }

      const rawValue = input.value;
      const trimmedValue = rawValue.trim();

      if (!this.#hasValidCharacters(rawValue)) {
        input.setCustomValidity(
          input.dataset.invalidCharsMessage || 'Erlaubt sind Buchstaben, Zahlen, Leerzeichen und . - & +'
        );
        input.reportValidity();
        return;
      }

      input.setCustomValidity('');
      engravings.push(trimmedValue);
    }

    const visiblePropertyKey = this.#getVisiblePropertyKey();
    const bundlePlan = `bundle_${selectedPlanQty}`;
    const previewVersion = this.refs.previewVersionProperty.value || 'v1';
    const bundleGroupId = this.#bundleGroupId;
    const personalizedCount = engravings.filter((value) => value.length > 0).length;

    if (personalizedCount > 0 && !this.#hasEngravingFeeConfigured()) {
      this.#showInlineError(this.#getEngravingFeeMissingMessage());
      return;
    }

    /** @type {Array<any>} */
    const items = [];

    for (let slot = 1; slot <= selectedPlanQty; slot += 1) {
      const engravingText = (engravings[slot - 1] || '').trim();
      const variantId = this.#normalizeVariantId(this.#bundleSlotVariantIds[slot - 1] || this.#activeVariantId || '');
      if (!variantId || !/^\d+$/.test(variantId)) {
        this.#showInlineError(`Variante fuer Flasche ${slot} konnte nicht aufgeloest werden.`);
        return;
      }

      const color = this.#resolveColorForSlot(slot);
      const configId = this.#resolveConfigIdForSlot(slot);
      const item = {
        id: variantId,
        quantity: 1,
      };

      if (engravingText) {
        item.properties = {
          [visiblePropertyKey]: engravingText,
          _personalized: '1',
          _engraving_text: engravingText,
          _engraving_slot: String(slot),
          _engraving_color: color,
          _engraving_config_id: configId,
          _bundle_group_id: bundleGroupId,
          _engraving_group_id: bundleGroupId,
          _bundle_plan: bundlePlan,
          _preview_version: previewVersion,
          [`_engraving_text_${slot}`]: engravingText,
          [`_engraving_color_${slot}`]: color,
          [`_engraving_config_id_${slot}`]: configId,
        };
      }

      items.push(item);
    }

    const feeItem = this.#buildEngravingFeeItem(personalizedCount, bundleGroupId);
    if (feeItem) {
      items.push(feeItem);
    }

    if (items.length === 0) {
      this.#showInlineError('Bundle konnte nicht hinzugefuegt werden.');
      return;
    }

    const sectionIds = this.#getCartBundledSectionIds();

    /** @type {{items: Array<any>, sections?: string}} */
    const payload = { items };
    if (sectionIds.length > 0) {
      payload.sections = sectionIds.join(',');
    }
    const sectionsUrl = this.#getSectionsUrlForVariant(this.#activeVariantId || '');
    if (sectionsUrl) {
      payload.sections_url = sectionsUrl;
    }

    const addToCartButton = this.#form.querySelector('[ref="addToCartButton"]');

    this.#isSubmittingBundle = true;
    clearEngravingCartInjection();
    this.#clearInlineError();
    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = true;
    }

    try {
      const response = await fetch(
        this.#getCartAddUrl(),
        fetchConfig('json', {
          body: JSON.stringify(payload),
        })
      );

      const responseJson = await response.json();

      if (responseJson.status) {
        this.#showInlineError(responseJson.message || 'Bundle konnte nicht hinzugefügt werden.');
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, this.#activeVariantId || '', {
          source: 'product-form-component',
          itemCount: items.length,
          productId: this.#form.closest('product-form-component')?.dataset.productId,
          sections: responseJson.sections,
        })
      );
      await this.#dispatchThemeCartChange(responseJson, this.#activeVariantId || '', sectionIds);
      this.#clearInlineError();
    } catch (error) {
      console.error(error);
      this.#showInlineError('Bundle konnte nicht hinzugefügt werden. Bitte erneut versuchen.');
      this.#dispatchThemeCartError('Bundle konnte nicht hinzugefügt werden. Bitte erneut versuchen.');
    } finally {
      this.#isSubmittingBundle = false;
      if (addToCartButton instanceof HTMLButtonElement) {
        addToCartButton.disabled = false;
      }
    }
  }

  /**
   * @param {number} selectedPlanQty
   * @param {boolean} [shouldReportValidity]
   * @returns {string[] | null}
   */
  #collectBundleEngravings(selectedPlanQty, shouldReportValidity = true) {
    const values = [];
    const namesRequired = this.dataset.bundleNamesRequired !== 'false';

    for (let slot = 1; slot <= selectedPlanQty; slot += 1) {
      const input = this.#getSlotInput(slot);
      if (!(input instanceof HTMLInputElement)) return null;

      const rawValue = input.value;
      const trimmedValue = rawValue.trim();

      if (!this.#hasValidCharacters(rawValue)) {
        input.setCustomValidity(
          input.dataset.invalidCharsMessage || 'Erlaubt sind Buchstaben, Zahlen, Leerzeichen und . - & +'
        );
        if (shouldReportValidity) {
          input.reportValidity();
        }
        return null;
      }

      if (namesRequired && trimmedValue.length === 0) {
        input.setCustomValidity('Bitte Name eingeben.');
        if (shouldReportValidity) {
          input.reportValidity();
        }
        return null;
      }

      input.setCustomValidity('');
      values.push(trimmedValue);
    }

    return values;
  }

  /**
   * @param {number} selectedPlanQty
   * @param {boolean} shouldReportValidity
   * @returns {boolean}
   */
  #prepareExternalBundleProperties(selectedPlanQty, shouldReportValidity) {
    const engravings = this.#collectBundleEngravings(selectedPlanQty, shouldReportValidity);
    if (!engravings) {
      clearEngravingCartInjection();
      this.#clearPrimaryProperties();
      this.#setPropertyInputsEnabled(false);
      this.#clearBundleSlotProperties();
      return false;
    }

    const summaryParts = [];
    for (let slot = 1; slot <= selectedPlanQty; slot += 1) {
      const value = engravings[slot - 1] || '';
      if (!value) continue;
      summaryParts.push(`${slot}: ${value}`);
    }

    if (summaryParts.length === 0) {
      clearEngravingCartInjection();
      this.#clearPrimaryProperties();
      this.#setPropertyInputsEnabled(false);
      this.#clearBundleSlotProperties();
      return false;
    }

    const configId = this.#resolveConfigIdForSlot(1);

    this.refs.visibleProperty.value = summaryParts.join(' | ');
    this.refs.engravingTextProperty.value = summaryParts.join(' | ');
    this.refs.personalizedProperty.value = '1';
    this.refs.engravingSlotProperty.value = 'multi';
    this.refs.engravingConfigIdProperty.value = configId;
    this.refs.bundleGroupIdProperty.value = this.#bundleGroupId;
    this.refs.bundlePlanProperty.value = `bundle_${selectedPlanQty}`;
    this.#setPropertyInputsEnabled(true);

    this.#clearBundleSlotProperties();

    const slotTextProperties = Array.isArray(this.refs.bundleSlotTextProperties) ? this.refs.bundleSlotTextProperties : [];
    const slotColorProperties = Array.isArray(this.refs.bundleSlotColorProperties) ? this.refs.bundleSlotColorProperties : [];
    const slotConfigProperties = Array.isArray(this.refs.bundleSlotConfigProperties) ? this.refs.bundleSlotConfigProperties : [];
    const injectionLines = [];

    for (let slot = 1; slot <= selectedPlanQty; slot += 1) {
      const value = (engravings[slot - 1] || '').trim();
      if (!value) continue;

      const color = this.#resolveColorForSlot(slot);
      const slotConfigId = this.#resolveConfigIdForSlot(slot);
      const slotVariantId = this.#normalizeVariantId(this.#bundleSlotVariantIds[slot - 1] || this.#activeVariantId || '');

      const slotTextInput = slotTextProperties[slot - 1];
      if (slotTextInput instanceof HTMLInputElement) {
        slotTextInput.value = value;
        slotTextInput.disabled = false;
      }

      const slotColorInput = slotColorProperties[slot - 1];
      if (slotColorInput instanceof HTMLInputElement) {
        slotColorInput.value = color;
        slotColorInput.disabled = false;
      }

      const slotConfigInput = slotConfigProperties[slot - 1];
      if (slotConfigInput instanceof HTMLInputElement) {
        slotConfigInput.value = slotConfigId;
        slotConfigInput.disabled = false;
      }

      injectionLines.push({
        slot,
        text: value,
        color,
        configId: slotConfigId,
        variantId: slotVariantId,
      });
    }

    queueEngravingCartInjection({
      visiblePropertyKey: this.#getVisiblePropertyKey(),
      bundleGroupId: this.#bundleGroupId,
      engravingGroupId: this.#bundleGroupId,
      bundlePlan: `bundle_${selectedPlanQty}`,
      previewVersion: this.refs.previewVersionProperty.value || 'v1',
      lines: injectionLines,
    });

    return true;
  }

  /**
   * @param {number} slot
   * @returns {HTMLInputElement | null}
   */
  #getSlotInput(slot) {
    if (slot === 1) {
      return this.refs.textInput;
    }

    const input = this.querySelector(`[data-slot-input-index="${slot}"]`);
    return input instanceof HTMLInputElement ? input : null;
  }

  #syncSlotCounters() {
    const slotCounters = Array.from(this.querySelectorAll('[data-slot-counter-index]'));

    for (const counter of slotCounters) {
      if (!(counter instanceof HTMLElement)) continue;

      const slot = this.#toNumber(counter.dataset.slotCounterIndex, 1);
      const input = this.#getSlotInput(slot);
      const length = input instanceof HTMLInputElement ? input.value.length : 0;
      counter.textContent = `${length}/10`;
    }
  }

  /**
   * @returns {number}
   */
  #getSelectedPlanQuantity() {
    if (this.#useExternalBundleApp) {
      return Math.max(1, Math.min(5, this.#selectedPlanQty || 1));
    }

    const inputs = Array.isArray(this.refs.bundlePlanInputs) ? this.refs.bundlePlanInputs : [];
    if (inputs.length === 0) {
      return Math.max(1, Math.min(5, this.#selectedPlanQty || 1));
    }

    const checkedInput = inputs.find((input) => input.checked) || inputs[0];

    if (!(checkedInput instanceof HTMLInputElement)) {
      return Math.max(1, Math.min(5, this.#selectedPlanQty || 1));
    }

    const parsed = this.#toNumber(checkedInput.value, 1);
    this.#selectedPlanQty = Math.max(1, Math.min(5, parsed));
    return this.#selectedPlanQty;
  }

  /**
   * @returns {string}
   */
  #getVisiblePropertyKey() {
    const fullName = this.refs.visibleProperty.getAttribute('name') || 'properties[Gravur]';
    const matches = fullName.match(/^properties\[(.+)\]$/);
    return matches ? matches[1] : 'Gravur';
  }

  /**
   * @param {number} slotIndex
   * @param {boolean} isBundle
   * @param {string} singleLabel
   * @param {string} multiTemplate
   * @returns {string}
   */
  #buildSlotLabel(slotIndex, isBundle, singleLabel, multiTemplate) {
    if (!isBundle) return singleLabel;

    const colorLabel = this.#bundleSlotColors[slotIndex - 1];
    const baseLabel = multiTemplate.replace('[index]', String(slotIndex));
    if (!colorLabel) return baseLabel;

    return `${baseLabel} - ${colorLabel}`;
  }

  /**
   * @param {number} slotIndex
   * @returns {string}
   */
  #buildSlotTabLabel(slotIndex) {
    const color = this.#resolveColorForSlot(slotIndex).trim();
    if (color.length > 0) return color;

    return `Flasche ${slotIndex}`;
  }

  /**
   * @param {Element} tabButton
   * @param {number} slotIndex
   * @param {boolean} isVisible
   */
  #syncSlotTabIcon(tabButton, slotIndex, isVisible) {
    const icon = tabButton.querySelector('.engraving-personalizer__slot-tab-icon');
    if (!(icon instanceof HTMLElement)) return;

    if (!isVisible) {
      icon.style.removeProperty('--engraving-slot-icon-bg');
      return;
    }

    const color = this.#resolveColorForSlot(slotIndex).trim();
    const iconBackground = this.#resolveIconBackgroundFromColor(color);

    if (iconBackground) {
      icon.style.setProperty('--engraving-slot-icon-bg', iconBackground);
      return;
    }

    icon.style.removeProperty('--engraving-slot-icon-bg');
  }

  /**
   * @param {string} colorLabel
   * @returns {string}
   */
  #resolveIconBackgroundFromColor(colorLabel) {
    const swatchBackground = this.#findSwatchBackgroundForColor(colorLabel);
    if (swatchBackground) return swatchBackground;

    return this.#resolveColorTokenToCss(colorLabel);
  }

  /**
   * @param {string} colorLabel
   * @returns {string}
   */
  #findSwatchBackgroundForColor(colorLabel) {
    const normalizedColor = this.#normalizeValue(colorLabel);
    if (!normalizedColor) return '';

    const sectionRoot = this.closest('.shopify-section') || document;
    const swatchLabels = sectionRoot.querySelectorAll('.variant-option--swatches .variant-option__button-label');

    for (const swatchLabel of swatchLabels) {
      if (!(swatchLabel instanceof HTMLElement)) continue;

      const swatchInput = swatchLabel.querySelector('input[type="radio"]');
      if (!(swatchInput instanceof HTMLInputElement)) continue;

      const inputValue = (swatchInput.value || swatchInput.getAttribute('aria-label') || '').trim();
      if (this.#normalizeValue(inputValue) !== normalizedColor) continue;

      const swatch = swatchLabel.querySelector('.swatch');
      const swatchBackground = this.#readSwatchBackground(swatch);
      if (swatchBackground) return swatchBackground;
    }

    return '';
  }

  /**
   * @param {Element | null} swatch
   * @returns {string}
   */
  #readSwatchBackground(swatch) {
    if (!(swatch instanceof HTMLElement)) return '';

    const inlineBackground = swatch.style.getPropertyValue('--swatch-background').trim();
    if (this.#isValidSwatchBackground(inlineBackground)) return inlineBackground;

    const computedBackground = getComputedStyle(swatch).getPropertyValue('--swatch-background').trim();
    if (this.#isValidSwatchBackground(computedBackground)) return computedBackground;

    return '';
  }

  /**
   * @param {string} value
   * @returns {boolean}
   */
  #isValidSwatchBackground(value) {
    if (!value) return false;
    return value !== 'none' && value !== 'null';
  }

  /**
   * @param {string} colorLabel
   * @returns {string}
   */
  #resolveColorTokenToCss(colorLabel) {
    const rawColor = (colorLabel || '').trim();
    if (!rawColor) return '';

    if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('color', rawColor)) {
      return rawColor;
    }

    const lowerColor = rawColor.toLowerCase();
    const namedColorMap = {
      obsidian: '#1a1c21',
      black: '#26272b',
      gray: '#8f9297',
      grey: '#8f9297',
      steel: '#c2c6cb',
      pearl: '#d8dde2',
      silver: '#c8ccd1',
      blue: '#b7e4fb',
      nordic: '#b7e4fb',
      'saint rose': '#f3d4df',
      saintrose: '#f3d4df',
      rose: '#f3d4df',
      pink: '#f3d4df',
      cloud: '#edf1f5',
      white: '#f5f7f8',
      cream: '#dfd6c2',
      dune: '#d4c5a7',
      beige: '#d8c7ac',
      sand: '#cfbe9f',
    };

    for (const [token, cssColor] of Object.entries(namedColorMap)) {
      if (lowerColor.includes(token)) return cssColor;
    }

    const tokens = lowerColor.split(/[\s/_-]+/).filter(Boolean);
    for (const token of tokens) {
      if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('color', token)) {
        return token;
      }
    }

    return this.#buildDeterministicColor(rawColor);
  }

  /**
   * @param {string} seed
   * @returns {string}
   */
  #buildDeterministicColor(seed) {
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
      hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
    }

    const normalizedHash = Math.abs(hash);
    const hue = normalizedHash % 360;
    const saturation = 24 + (normalizedHash % 19);
    const lightness = 58 + (normalizedHash % 10);

    return `hsl(${hue} ${saturation}% ${lightness}%)`;
  }

  #syncInternalVariantSelectors() {
  const selectedPlanQty = this.#getSelectedPlanQuantity();
  const selects = Array.isArray(this.refs.bundleSlotVariantSelects)
    ? this.refs.bundleSlotVariantSelects
    : [];

  for (const select of selects) {
    if (!(select instanceof HTMLSelectElement)) continue;

    const slot = this.#toNumber(select.dataset.slotVariantIndex, 1);
    const shouldShow = slot >= 1 && slot <= selectedPlanQty;
    const wrapper = select.closest('.engraving-personalizer__variant-picker');

    if (wrapper instanceof HTMLElement) {
      wrapper.toggleAttribute('hidden', !shouldShow);
    }

    if (!shouldShow) continue;

    const selectedOption = select.selectedOptions?.[0];
    const variantId = this.#normalizeVariantId(select.value);
    const color = (
      selectedOption?.dataset.color ||
      selectedOption?.textContent ||
      ''
    ).trim();

    this.#bundleSlotVariantIds[slot - 1] = variantId;
    this.#bundleSlotColors[slot - 1] = color;
  }

  this.#syncSwatchUI();
}

/**
 * @param {number} slot
 * @returns {string}
 */
#getVariantIdForSlot(slot) {
  this.#syncInternalVariantSelectors();

  const selectedFromSlot = this.#normalizeVariantId(this.#bundleSlotVariantIds[slot - 1] || '');
  if (selectedFromSlot) return selectedFromSlot;

  return this.#normalizeVariantId(this.#activeVariantId || this.#getVariantIdFromForm() || '');
}

/**
 * @param {boolean} isLoading
 */
#setModalButtonLoading(isLoading) {
  const button = this.refs.modalAddToCartButton;
  if (!(button instanceof HTMLButtonElement)) return;

  button.disabled = isLoading;
  button.textContent = isLoading ? 'Wird hinzugefügt…' : 'Speichern & in den Warenkorb';
}

        #syncFromExternalBundleApp() {
      this.#refreshActiveVariantFromForm();

      const externalRoot = this.#getExternalBundleRoot();
      const bundleSelector = this.refs.bundleSelectorContainer;
      const shouldShowInternalBundleSelector = this.dataset.showBundleSelector === 'true';

      if (shouldShowInternalBundleSelector) {
        this.#useExternalBundleApp = false;
        this.#externalBundleRoot = null;

        if (bundleSelector instanceof HTMLElement) {
          bundleSelector.hidden = false;
        }

        this.#syncInternalVariantSelectors();
        this.#resetExternalBundleTotalPreview();
        return;
      }

      if (!(externalRoot instanceof HTMLElement)) {
        this.#useExternalBundleApp = false;
        this.#externalBundleRoot = null;
        this.#selectedPlanQty = 1;
        this.#bundleSlotColors = [];
        this.#bundleSlotVariantIds = [];
        this.#activePreviewSlot = 1;

        clearEngravingCartInjection();

        if (bundleSelector instanceof HTMLElement) {
          bundleSelector.hidden = true;
        }

        this.#resetExternalBundleTotalPreview();
        return;
      }

      this.#useExternalBundleApp = true;
      this.#externalBundleRoot = externalRoot;

      if (bundleSelector instanceof HTMLElement) {
        bundleSelector.hidden = true;
      }

      const selection = this.#readExternalBundleSelection(externalRoot);
      if (!selection) return;

      this.#setInternalPlanQuantity(selection.quantity);
      this.#bundleSlotColors = selection.colors.slice(0, selection.quantity);

      this.#bundleSlotVariantIds = this.#bundleSlotColors.map((color, index) => {
        const variantIdFromSelection = this.#normalizeVariantId(selection.variantIds[index] || '');
        if (variantIdFromSelection) return variantIdFromSelection;

        const variantIdFromColor = this.#normalizeVariantId(this.#resolveVariantIdForColor(color));
        if (variantIdFromColor) return variantIdFromColor;

        return this.#normalizeVariantId(this.#activeVariantId || '');
      });

      this.#activePreviewSlot = Math.max(1, Math.min(selection.quantity, this.#activePreviewSlot));

      this.#syncBundlePlanState();
      this.#applyBundleSlotPreview(this.#activePreviewSlot);
      this.#syncExternalBundleTotalPreview();
    }



  #refreshActiveVariantFromForm() {
    const variantIdFromForm = this.#normalizeVariantId(this.#getVariantIdFromForm());
    if (!variantIdFromForm) return;
    if (variantIdFromForm === this.#activeVariantId) return;

    this.#activeVariantId = variantIdFromForm;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getExternalBundleRoot() {
    const selectors = [
      '[id*="pumper_bundles_app_block_qb"]',
      '[id*="pumper_bundles_app_block_bundle"]',
      '[id*="pumper_bundles_app_block_"]',
      '[id*="pumper-bundles"]',
      '[class*="pumper-bundles"]',
      '[class*="pumper_bundle"]',
      '[id*="bundler_bundler_target_element"]',
      '[id*="bundler-target-element"]',
      '[class*="bundler-target-element"]',
      '[class*="bundler_target_element"]',
      '[class*="bundler-bundle"]',
    ];

    const section = this.closest('.shopify-section');
    const scopes = [section, document];

    for (const scope of scopes) {
      for (const selector of selectors) {
        const matches = scope?.querySelectorAll(selector) || [];
        for (const match of matches) {
          if (!(match instanceof HTMLElement)) continue;
          if (!this.#isExternalBundleCandidate(match, selector)) continue;
          return match;
        }
      }

      const structuredMatch = this.#findBundleRootByStructure(scope || document);
      if (structuredMatch instanceof HTMLElement) {
        return structuredMatch;
      }
    }

    return null;
  }

  /**
   * @param {HTMLElement} candidate
   * @param {string} [sourceSelector]
   * @returns {boolean}
   */
  #isExternalBundleCandidate(candidate, sourceSelector = '') {
    if (candidate === this || candidate.closest('engraving-personalizer-component') === this) {
      return false;
    }

    const radioCount = candidate.querySelectorAll('input[type="radio"]').length;
    if (radioCount === 0) return false;

    const selectCount = candidate.querySelectorAll('select').length;
    if (selectCount > 0) return true;

    if (sourceSelector.includes('pumper') || sourceSelector.includes('bundler')) {
      return true;
    }

    const textContent = (candidate.textContent || '').toLowerCase();
    const hasQuantity = this.#extractPlanQtyFromText(textContent) > 0;
    const hasBundleTerms = /(rabatt|discount|bundle|fit-week|save|versand|popular|beliebtesten)/i.test(textContent);

    return hasQuantity && hasBundleTerms;
  }

  /**
   * @param {HTMLElement | Document} scope
   * @returns {HTMLElement | null}
   */
  #findBundleRootByStructure(scope) {
    const checkedRadios = Array.from(scope.querySelectorAll('input[type="radio"]:checked'));
    const scopeBoundary = scope instanceof HTMLElement ? scope : null;

    for (const checkedRadio of checkedRadios) {
      if (!(checkedRadio instanceof HTMLInputElement)) continue;

      let current = checkedRadio.parentElement;
      while (current instanceof HTMLElement) {
        if (this.#isExternalBundleCandidate(current)) {
          return current;
        }

        if (scopeBoundary && current === scopeBoundary) break;
        current = current.parentElement;
      }
    }

    return null;
  }

  /**
   * @param {HTMLElement} root
   * @returns {{ quantity: number, colors: string[], variantIds: string[] } | null}
   */
  #readExternalBundleSelection(root) {
    const checkedRadio = this.#findCheckedBundlePlanRadio(root);
    const selectedCard = this.#findSelectedBundleCard(checkedRadio, root);
    const quantityText = selectedCard?.textContent || checkedRadio?.closest('label')?.textContent || root.textContent || '';
    let quantity = this.#extractPlanQtyFromText(quantityText);

    let colorSelects = selectedCard?.querySelectorAll('select') || [];
    if (colorSelects.length === 0) {
      colorSelects = root.querySelectorAll('select');
    }

    const selectElements = Array.from(colorSelects);
    const colors = this.#extractColorValuesFromSelects(selectElements, quantity);
    const variantIds = this.#extractVariantIdsFromSelects(selectElements, quantity);

    if (!quantity && colors.length > 0) {
      quantity = colors.length;
    }
    if (!quantity && variantIds.length > 0) {
      quantity = variantIds.length;
    }

    if (!quantity) {
      quantity = 1;
    }

    quantity = Math.max(1, Math.min(5, quantity));

    const fallbackColor = this.#getCurrentVariantColor();
    while (colors.length < quantity) {
      colors.push(fallbackColor);
    }
    while (variantIds.length < quantity) {
      variantIds.push('');
    }

    return {
      quantity,
      colors: colors.slice(0, quantity),
      variantIds: variantIds.slice(0, quantity),
    };
  }

  /**
   * @param {Element[]} selectElements
   * @param {number} expectedQty
   * @returns {string[]}
   */
  #extractColorValuesFromSelects(selectElements, expectedQty) {
    const selectedValues = selectElements
      .map((select) => {
        if (!(select instanceof HTMLSelectElement)) return '';
        const selectedOption = select.selectedOptions?.[0];
        return (selectedOption?.textContent || select.value || '').trim();
      })
      .filter((value) => value.length > 0);

    if (selectedValues.length === 0) return [];

    const targetQty = Math.max(0, this.#toNumber(expectedQty, 0));
    const knownColors = this.#getKnownColorLabels();

    const valuesMatchingKnownColors = selectedValues.filter((value) => {
      return knownColors.has(this.#normalizeValue(value));
    });
    if (valuesMatchingKnownColors.length > 0) {
      if (targetQty > 0 && valuesMatchingKnownColors.length >= targetQty) {
        return valuesMatchingKnownColors.slice(0, targetQty);
      }
      return valuesMatchingKnownColors;
    }

    const valuesWithoutSizeTokens = selectedValues.filter((value) => !this.#looksLikeSizeValue(value));
    if (valuesWithoutSizeTokens.length > 0) {
      if (targetQty > 0 && valuesWithoutSizeTokens.length >= targetQty) {
        return valuesWithoutSizeTokens.slice(0, targetQty);
      }
      return valuesWithoutSizeTokens;
    }

    // Last-resort fallback for two-column bundle UIs (Farbe | Größe): pick every first select of a pair.
    const alternatingValues = selectedValues.filter((_, index) => index % 2 === 0);
    if (alternatingValues.length > 0) {
      if (targetQty > 0 && alternatingValues.length >= targetQty) {
        return alternatingValues.slice(0, targetQty);
      }
      return alternatingValues;
    }

    return selectedValues;
  }

  /**
   * @param {Element[]} selectElements
   * @param {number} expectedQty
   * @returns {string[]}
   */
  #extractVariantIdsFromSelects(selectElements, expectedQty) {
    const selectedValues = selectElements
      .map((select) => {
        if (!(select instanceof HTMLSelectElement)) return '';
        const selectedOption = select.selectedOptions?.[0];
        const optionValue = (selectedOption?.value || select.value || '').trim();
        return this.#normalizeVariantId(optionValue);
      })
      .filter((value) => value.length > 0);

    if (selectedValues.length === 0) return [];

    const targetQty = Math.max(0, this.#toNumber(expectedQty, 0));
    const uniqueValues = [];
    for (const value of selectedValues) {
      if (uniqueValues.includes(value)) continue;
      uniqueValues.push(value);
    }

    if (targetQty > 0) {
      if (uniqueValues.length >= targetQty) {
        return uniqueValues.slice(0, targetQty);
      }

      if (selectedValues.length >= targetQty) {
        const step = Math.max(1, Math.floor(selectedValues.length / targetQty));
        const sampledValues = [];
        for (let i = 0; i < selectedValues.length && sampledValues.length < targetQty; i += step) {
          sampledValues.push(selectedValues[i]);
        }
        if (sampledValues.length >= targetQty) {
          return sampledValues.slice(0, targetQty);
        }
      }
    }

    return uniqueValues.length > 0 ? uniqueValues : selectedValues;
  }

  /**
   * @returns {Set<string>}
   */
  #getKnownColorLabels() {
    const knownColors = new Set();

    for (const variantData of Object.values(this.#variantOptionsById)) {
      if (!variantData || typeof variantData !== 'object') continue;

      const candidates = [variantData.option1, variantData.option2, variantData.option3];
      for (const candidate of candidates) {
        const value = (candidate || '').toString().trim();
        if (!value) continue;
        if (this.#looksLikeSizeValue(value)) continue;

        const normalized = this.#normalizeValue(value);
        if (normalized) {
          knownColors.add(normalized);
        }
      }
    }

    return knownColors;
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLInputElement | null}
   */
  #findCheckedBundlePlanRadio(root) {
    const checkedRadios = Array.from(root.querySelectorAll('input[type="radio"]:checked'));
    if (checkedRadios.length === 0) return null;

    for (const radio of checkedRadios) {
      if (!(radio instanceof HTMLInputElement)) continue;

      const contextText = radio.closest('label')?.textContent || radio.parentElement?.textContent || radio.value || '';
      if (this.#extractPlanQtyFromText(contextText) > 0) {
        return radio;
      }
    }

    const fallback = checkedRadios[0];
    return fallback instanceof HTMLInputElement ? fallback : null;
  }

  /**
   * @param {Element | null | undefined} checkedRadio
   * @param {HTMLElement} root
   * @returns {HTMLElement | null}
   */
  #findSelectedBundleCard(checkedRadio, root) {
    if (!(checkedRadio instanceof Element)) return null;

    let current = checkedRadio.parentElement;
    while (current && current !== root) {
      if (current.querySelectorAll('select').length > 0) {
        return current;
      }
      current = current.parentElement;
    }

    return checkedRadio.closest('label');
  }

  /**
   * @param {string} text
   * @returns {number}
   */
  #extractPlanQtyFromText(text) {
    if (!text) return 0;

    const normalized = text.toLowerCase();
    const match = normalized.match(/(\d+)\s*(pair|stück|stueck|pieces?|pcs|set|flaschen?|bottles?)/i);
    if (match) {
      return this.#toNumber(match[1], 0);
    }

    const promoMatch = normalized.match(/\b([2-5])\b(?=[^\n]{0,30}(rabatt|discount|off|save|versand|popular|beliebtesten))/i);
    if (promoMatch) {
      return this.#toNumber(promoMatch[1], 0);
    }

    return 0;
  }

  /**
   * @param {number} quantity
   */
  #setInternalPlanQuantity(quantity) {
    const inputs = Array.isArray(this.refs.bundlePlanInputs) ? this.refs.bundlePlanInputs : [];

    const normalized = Math.max(1, Math.min(5, quantity));
    this.#selectedPlanQty = normalized;

    if (inputs.length === 0) return;

    const matchingInput = inputs.find((input) => this.#toNumber(input.value, 1) === normalized);
    const fallbackInput = matchingInput || inputs[0];
    if (fallbackInput instanceof HTMLInputElement) {
      fallbackInput.checked = true;
    }
  }

  /**
   * @param {number} slot
   */
  #applyBundleSlotPreview(slot) {
  const selectedVariantId = this.#normalizeVariantId(this.#bundleSlotVariantIds[slot - 1] || '');

  if (selectedVariantId) {
    this.#applyLayoutForVariant(selectedVariantId);
    return;
  }

  this.#applyLayoutForVariant(this.#activeVariantId);
}

  /**
   * @param {string} color
   * @returns {string | null}
   */
  #resolveVariantIdForColor(color) {
    if (this.#looksLikeSizeValue(color)) {
      return null;
    }

    const normalizedColor = this.#normalizeValue(color);
    if (!normalizedColor) return null;

    for (const [variantId, variantData] of Object.entries(this.#variantOptionsById)) {
      if (this.#normalizeValue(variantData.option1) === normalizedColor) {
        return variantId;
      }
    }

    for (const [variantId, variantData] of Object.entries(this.#variantOptionsById)) {
      if (this.#normalizeValue(variantData.title).includes(normalizedColor)) {
        return variantId;
      }
    }

    // Optional fallback for shops where color is not option1:
    // consider option2/3 only when they don't look like sizes.
    for (const [variantId, variantData] of Object.entries(this.#variantOptionsById)) {
      const optionCandidates = [variantData.option2, variantData.option3];
      if (
        optionCandidates.some((value) => {
          const candidate = (value || '').toString().trim();
          if (!candidate || this.#looksLikeSizeValue(candidate)) return false;
          return this.#normalizeValue(candidate) === normalizedColor;
        })
      ) {
        return variantId;
      }
    }

    return null;
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  #normalizeVariantId(value) {
    if (value == null) return '';

    const rawValue = String(value).trim();
    if (!rawValue) return '';

    if (Object.prototype.hasOwnProperty.call(this.#variantOptionsById, rawValue)) {
      return rawValue;
    }

    const gidMatch = rawValue.match(/ProductVariant\/(\d+)/i);
    if (gidMatch) {
      const gidId = gidMatch[1];
      if (Object.prototype.hasOwnProperty.call(this.#variantOptionsById, gidId)) {
        return gidId;
      }
      return gidId;
    }

    if (/^\d+$/.test(rawValue)) return rawValue;

    const numericMatch = rawValue.match(/\b(\d{8,})\b/);
    if (numericMatch) {
      const numericId = numericMatch[1];
      if (Object.prototype.hasOwnProperty.call(this.#variantOptionsById, numericId)) {
        return numericId;
      }
      return numericId;
    }

    return '';
  }

  /**
   * @param {number} slot
   * @returns {string}
   */
  #resolveColorForSlot(slot) {
    const explicitColor = (this.#bundleSlotColors[slot - 1] || '').trim();
    if (explicitColor) return explicitColor;

    const variantId = this.#bundleSlotVariantIds[slot - 1];
    if (variantId) {
      const variant = this.#variantOptionsById[variantId];
      const variantColor = (variant?.option1 || variant?.title || '').trim();
      if (variantColor) return variantColor;
    }

    return this.#getCurrentVariantColor();
  }

  /**
   * @param {number} slot
   * @returns {string}
   */
  #resolveConfigIdForSlot(slot) {
    const variantId = this.#bundleSlotVariantIds[slot - 1] || this.#activeVariantId;
    const layout = (variantId && this.#engravingByVariant[variantId]) || this.#defaultLayout;
    const configId = (layout?.configId || '').toString().trim();
    return configId || this.#getFallbackConfigId();
  }

  /**
   * @returns {string}
   */
  #getCurrentVariantColor() {
    if (!this.#activeVariantId) return 'Gray';

    const activeVariant = this.#variantOptionsById[this.#activeVariantId];
    return activeVariant?.option1 || activeVariant?.title || 'Gray';
  }

  /**
   * @returns {string[]}
   */
  #getCartBundledSectionIds() {
    const collectedSectionIds = [];

    try {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: {
            sections: collectedSectionIds,
          },
        })
      );
    } catch (error) {
      console.warn('Failed to collect bundled cart sections from theme event', error);
    }

    const fallbackSectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter((id) => Boolean(id));

    const cartDrawerSection = document
      .querySelector('cart-drawer')
      ?.closest('[id^="shopify-section-"]');
    if (cartDrawerSection instanceof HTMLElement) {
      const sectionId = cartDrawerSection.id.replace(/^shopify-section-/, '').trim();
      if (sectionId) {
        fallbackSectionIds.push(sectionId);
      }
    }

    const normalized = [...collectedSectionIds, ...fallbackSectionIds]
      .map((id) => (id || '').toString().trim())
      .filter((id) => id.length > 0);

    return Array.from(new Set(normalized));
  }

  /**
   * @param {string | null | undefined} variantId
   * @returns {string}
   */
  #getSectionsUrlForVariant(variantId) {
    const normalizedVariantId = this.#normalizeVariantId(variantId || this.#activeVariantId || this.#getVariantIdFromForm() || '');
    const root = (window.Shopify?.routes?.root || '/').toString();
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;

    if (!normalizedVariantId) {
      return window.location.pathname + window.location.search;
    }

    return `${normalizedRoot}variants/${normalizedVariantId}`;
  }

  /**
   * @param {Record<string, any>} responseJson
   * @param {string} variantId
   * @param {string[]} sectionIds
   */
  async #dispatchThemeCartChange(responseJson, variantId, sectionIds) {
    const root = (window.Shopify?.routes?.root || '/').toString();
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    const onSuccessDo = this.#form?.querySelector('[name="on_success"]')?.value || null;

    if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
      window.location.href = `${normalizedRoot}cart`;
      return;
    }

    /** @type {Record<string, string>} */
    const sections = responseJson?.sections && typeof responseJson.sections === 'object'
      ? { ...responseJson.sections }
      : {};

    for (const sectionId of sectionIds) {
      if (typeof sections[sectionId] === 'string' && sections[sectionId].length > 0) continue;

      try {
        const sectionHtml = await fetch(`${normalizedRoot}?section_id=${encodeURIComponent(sectionId)}`).then((res) => res.text());
        if (sectionHtml) {
          sections[sectionId] = sectionHtml;
        }
      } catch (error) {
        console.warn(`Failed to fetch section HTML for ${sectionId}`, error);
      }
    }

    let cartContent = {};
    try {
      cartContent = await fetch(`${normalizedRoot}cart.js`).then((res) => res.json());
    } catch (error) {
      console.warn('Failed to fetch cart.js after add-to-cart', error);
    }

    cartContent.sections = sections;

    const addedItems = Array.isArray(responseJson?.items)
      ? responseJson.items
      : (responseJson ? [responseJson] : []);

    this.#form?.dispatchEvent(
      new CustomEvent('variant:add', {
        bubbles: true,
        detail: {
          items: addedItems,
          cart: cartContent,
          onSuccessDo,
        },
      })
    );

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          onSuccessDo,
          cart: cartContent,
        },
      })
    );
  }

  /**
   * @param {string} message
   */
  #dispatchThemeCartError(message) {
    this.#form?.dispatchEvent(
      new CustomEvent('cart:error', {
        bubbles: true,
        detail: {
          error: message,
        },
      })
    );

    document.dispatchEvent(new CustomEvent('cart:refresh'));
  }

  /**
   * @param {string | undefined} value
   * @returns {string}
   */
  #normalizeValue(value) {
    return (value || '')
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[\s_\-/]+/g, '');
  }

  /**
   * @param {string | undefined} value
   * @returns {boolean}
   */
  #looksLikeSizeValue(value) {
    const rawValue = (value || '').toString().trim().toLowerCase();
    if (!rawValue) return false;

    return (
      /\b\d+(?:[.,]\d+)?\s*(ml|l|cl|dl|oz|kg|g|mg)\b/i.test(rawValue) ||
      /(^|\s)(größe|groesse|grösse|size)\b/i.test(rawValue)
    );
  }

  /**
   * @returns {{ planQty: number, slotValues: string[] }}
   */
  #captureDraftState() {
    const selectedPlanQty = this.#getSelectedPlanQuantity();
    const slotValues = [];

    for (let slot = 1; slot <= 5; slot += 1) {
      const input = this.#getSlotInput(slot);
      slotValues.push(input instanceof HTMLInputElement ? input.value : '');
    }

    return {
      planQty: selectedPlanQty,
      slotValues,
    };
  }

  /**
   * @param {{ planQty: number, slotValues: string[] }} draftState
   */
  #applyDraftState(draftState) {
    if (!draftState) return;

    const bundleInputs = Array.isArray(this.refs.bundlePlanInputs) ? this.refs.bundlePlanInputs : [];
    if (bundleInputs.length > 0) {
      const matchingInput = bundleInputs.find((input) => this.#toNumber(input.value, 1) === draftState.planQty);
      const fallbackInput = matchingInput || bundleInputs[0];
      if (fallbackInput instanceof HTMLInputElement) {
        fallbackInput.checked = true;
      }
    }

    for (let slot = 1; slot <= 5; slot += 1) {
      const input = this.#getSlotInput(slot);
      if (!(input instanceof HTMLInputElement)) continue;

      input.value = draftState.slotValues[slot - 1] || '';
      input.setCustomValidity('');
    }

    this.#syncBundlePlanState();
    this.#syncInternalVariantSelectors();
    this.#syncFromInput();
  }

  /**
   * @returns {boolean}
   */
  #validateModal() {
    const selectedPlanQty = this.#getSelectedPlanQuantity();
    const rawValue = this.refs.textInput.value;
    const trimmedValue = rawValue.trim();

    if (!this.#hasValidCharacters(rawValue)) {
      this.refs.textInput.setCustomValidity(
        this.refs.textInput.dataset.invalidCharsMessage ||
        'Erlaubt sind Buchstaben, Zahlen, Leerzeichen und . - & +'
      );
      this.refs.textInput.reportValidity();
      return false;
    }

    if (selectedPlanQty > 1) {
      const isValid = Boolean(this.#collectBundleEngravings(selectedPlanQty));
      if (isValid) {
        this.#clearInlineError();
      } else {
        this.#showInlineError('Bitte fülle für jede Flasche ein Namensfeld aus.');
      }
      return isValid;
    }

    if (this.refs.textInput.required && trimmedValue.length === 0) {
      this.refs.textInput.setCustomValidity('Bitte Name eingeben.');
      this.refs.textInput.reportValidity();
      return false;
    }

    this.refs.textInput.setCustomValidity('');
    this.#clearInlineError();
    return true;
  }

  /**
   * @param {boolean} isOpen
   */
  #setModalOpen(isOpen) {
    const wasOpen = this.#isModalOpen;
    this.#isModalOpen = isOpen;

    const modal = this.refs.modal;
    const usesNativeDialog = modal instanceof HTMLDialogElement && typeof modal.showModal === 'function';

    if (usesNativeDialog) {
      if (isOpen) {
        if (!modal.open) {
          modal.showModal();
        }
      } else if (modal.open) {
        modal.close();
      }
      modal.setAttribute('aria-hidden', String(!isOpen));
    } else {
      modal.hidden = !isOpen;
      modal.style.display = isOpen ? 'flex' : 'none';
      modal.setAttribute('aria-hidden', String(!isOpen));
    }

    this.refs.openPersonalizerButton.setAttribute('aria-expanded', String(isOpen));

    if (isOpen && !wasOpen) {
      this.#previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.body.classList.add(BODY_MODAL_OPEN_CLASS);
      document.addEventListener('keydown', this.#onDocumentKeydown);
      if (!usesNativeDialog) {
        window.addEventListener('resize', this.#onWindowResize);
        window.requestAnimationFrame(() => this.#normalizeModalToViewport());
      }
      return;
    }

    if (!isOpen && wasOpen) {
      if (!usesNativeDialog) {
        this.#resetModalViewportNormalization();
        window.removeEventListener('resize', this.#onWindowResize);
      }
      document.body.style.overflow = this.#previousBodyOverflow;
      document.body.classList.remove(BODY_MODAL_OPEN_CLASS);
      document.removeEventListener('keydown', this.#onDocumentKeydown);
    }
  }

  #normalizeModalToViewport() {
    const modal = this.refs.modal;
    if (!(modal instanceof HTMLElement)) return;
    if (modal.hidden) return;

    const rect = modal.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;

    const topOffset = Math.round(rect.top);
    const leftOffset = Math.round(rect.left);
    const rightOffset = Math.round(viewportWidth - rect.right);
    const bottomOffset = Math.round(viewportHeight - rect.bottom);

    modal.style.top = `${-topOffset}px`;
    modal.style.left = `${-leftOffset}px`;
    modal.style.right = `${-rightOffset}px`;
    modal.style.bottom = `${-bottomOffset}px`;
  }

  #resetModalViewportNormalization() {
    const modal = this.refs.modal;
    if (!(modal instanceof HTMLElement)) return;

    modal.style.top = '';
    modal.style.left = '';
    modal.style.right = '';
    modal.style.bottom = '';
  }

  #resetPersonalizerAfterAddToCart() {
  const inputs = [
    this.refs.textInput,
    ...(Array.isArray(this.refs.bundleSlotInputs) ? this.refs.bundleSlotInputs : []),
  ];

  for (const input of inputs) {
    if (!(input instanceof HTMLInputElement)) continue;
    input.value = '';
    input.setCustomValidity('');
  }

  const bundleInputs = Array.isArray(this.refs.bundlePlanInputs) ? this.refs.bundlePlanInputs : [];
  const singlePlanInput = bundleInputs.find((input) => input.value === '1') || bundleInputs[0];

  if (singlePlanInput instanceof HTMLInputElement) {
    singlePlanInput.checked = true;
  }

  const variantSelects = Array.isArray(this.refs.bundleSlotVariantSelects)
    ? this.refs.bundleSlotVariantSelects
    : [];

  for (const select of variantSelects) {
    if (!(select instanceof HTMLSelectElement)) continue;
    select.selectedIndex = 0;
  }

  this.#selectedPlanQty = 1;
  this.#activePreviewSlot = 1;
  this.#bundleSlotColors = [];
  this.#bundleSlotVariantIds = [];
  this.#draftState = null;

  this.#clearPrimaryProperties();
  this.#setPropertyInputsEnabled(false);
  this.#clearBundleSlotProperties();
  clearEngravingCartInjection();

  this.refs.personalizedProperty.value = '';
  this.refs.visibleProperty.value = '';
  this.refs.engravingTextProperty.value = '';
  this.refs.engravingSlotProperty.value = '';
  this.refs.bundlePlanProperty.value = 'single';

  this.#syncBundlePlanState();
  this.#applyLayoutForVariant(this.#activeVariantId);
  this.#syncSlotCounters();

  const placeholder = this.dataset.previewPlaceholder || '';
  this.refs.previewText.textContent = placeholder;
  this.#updatePreviewTextScaling();

  this.#setLauncherActiveState(false);
  this.refs.launcherSummary.textContent = this.dataset.launcherEmptySummary || 'Keine Gravur ausgewählt';
}

  #updateLauncherSummary() {
    const selectedPlanQty = this.#getSelectedPlanQuantity();
    const values = [];

    for (let slot = 1; slot <= selectedPlanQty; slot += 1) {
      const input = this.#getSlotInput(slot);
      if (!(input instanceof HTMLInputElement)) continue;

      const trimmedValue = input.value.trim();
      if (trimmedValue.length > 0) {
        values.push(trimmedValue);
      }
    }

    const emptySummary = this.dataset.launcherEmptySummary || 'Keine Gravur ausgewählt';
    const isActive = this.refs.personalizedProperty.value === '1';
    this.#setLauncherActiveState(isActive);

    if (values.length === 0) {
      this.refs.launcherSummary.textContent = emptySummary;
      return;
    }

    if (selectedPlanQty === 1) {
      this.refs.launcherSummary.textContent = values[0];
      return;
    }

    const listedValues = values.slice(0, 2).join(', ');
    const extraCount = values.length > 2 ? ` +${values.length - 2}` : '';
    this.refs.launcherSummary.textContent = `${values.length}/${selectedPlanQty}: ${listedValues}${extraCount}`;
  }

  /**
   * @param {boolean} isActive
   */
  #setLauncherActiveState(isActive) {
    const inactiveTitle = (this.dataset.launcherInactiveTitle || 'Gravur hinzufügen').trim();
    const activeTitle = (this.dataset.launcherActiveTitle || 'Gravur bearbeiten').trim();
    const activeBadge = (this.dataset.launcherActiveBadge || '● Aktiv').trim();

    this.refs.launcherTitle.textContent = isActive ? activeTitle : inactiveTitle;
    this.refs.launcherActiveBadge.textContent = activeBadge;
    this.refs.launcherActiveBadge.hidden = !isActive;
    this.classList.toggle('is-personalized', isActive);
  }

  #parseVariantOptionMap() {
    const rawJson = this.refs.variantOptionMap.textContent?.trim();
    if (!rawJson) return;

    try {
      const parsed = JSON.parse(rawJson);
      if (!parsed || typeof parsed !== 'object') return;

      this.#variantOptionsById = /** @type {Record<string, {title?: string, option1?: string, option2?: string, option3?: string}>} */ (parsed);
    } catch (error) {
      console.error('Failed to parse variant option map', error);
    }
  }

  #parseEngravingMap() {
    const rawJson = this.refs.variantEngravingMap.textContent?.trim();
    if (!rawJson) return;

    try {
      const parsed = JSON.parse(rawJson);
      if (!parsed || typeof parsed !== 'object') return;

      for (const [key, value] of Object.entries(parsed)) {
        if (!value || typeof value !== 'object') continue;

        if (key === '__default') {
          this.#defaultLayout = /** @type {EngravingLayout} */ (value);
        } else {
          this.#engravingByVariant[key] = /** @type {EngravingLayout} */ (value);
        }
      }
    } catch (error) {
      console.error('Failed to parse engraving layout map', error);
    }
  }

  /**
   * @param {string | null | undefined} variantId
   */
  #applyLayoutForVariant(variantId) {
    const layout = (variantId && this.#engravingByVariant[variantId]) || this.#defaultLayout;

    if (!layout || typeof layout !== 'object') return;

    const configId = (layout.configId || '').toString().trim() || this.#getFallbackConfigId();
    this.refs.engravingConfigIdProperty.value = configId;

    const assetBase = (layout.assetBase || '').toString();
    if (assetBase) {
      this.refs.previewImage.src = assetBase;
      this.refs.previewImage.hidden = false;
    } else {
      this.refs.previewImage.hidden = true;
    }

    const canvasWidth = this.#toNumber(layout.canvasWidth, 1200);
    const canvasHeight = this.#toNumber(layout.canvasHeight, 1200);
    this.refs.previewSvg.setAttribute('viewBox', `0 0 ${canvasWidth} ${canvasHeight}`);

    const textX = this.#toNumber(layout.textX, canvasWidth / 2);
    const textY = this.#toNumber(layout.textY, canvasHeight / 2);
    const textRotation = this.#toNumber(layout.textRotationDeg, 0);
    this.#textMaxWidth = this.#toNumber(layout.textMaxWidth, 0);
    const fontSize = this.#toNumber(layout.fontSizePx, 44);
    const letterSpacing = this.#toNumber(layout.letterSpacingEm, 0);
    const textColor = (layout.textColor || '#111111').toString();
    const fallbackFontFamily = "'Poppins', sans-serif";
    const rawFontFamily = (layout.fontFamily || '').toString().trim();
    const fontFamily = rawFontFamily && rawFontFamily.toLowerCase() !== 'inherit' ? rawFontFamily : fallbackFontFamily;
    const blendMode = (layout.blendMode || 'multiply').toString();
    const textOpacity = this.#toNumber(layout.textOpacity, 0.85);

    this.refs.previewText.setAttribute('x', String(textX));
    this.refs.previewText.setAttribute('y', String(textY));
    this.refs.previewText.setAttribute('fill', textColor);
    this.refs.previewText.setAttribute('font-size', String(fontSize));
    this.refs.previewText.setAttribute('font-family', fontFamily);
    this.refs.previewText.setAttribute('transform', `rotate(${textRotation} ${textX} ${textY})`);

    this.refs.previewText.style.mixBlendMode = blendMode;
    this.refs.previewText.style.opacity = String(textOpacity);
    this.refs.previewText.style.letterSpacing = `${letterSpacing}em`;

    this.#updatePreviewTextScaling();
  }

  /**
   * @returns {string | null}
   */
  #getVariantIdFromForm() {
    const form = this.#getProductForm();
    if (!form) return null;

    const variantIdInput = form.querySelector('input[name="id"]');
    if (!(variantIdInput instanceof HTMLInputElement) || !variantIdInput.value) return null;

    return this.#normalizeVariantId(variantIdInput.value);
  }

  /**
   * @returns {string}
   */
  #getCartAddUrl() {
    const themeCartAddUrl = window.Theme?.routes?.cart_add_url;
    if (typeof themeCartAddUrl === 'string' && themeCartAddUrl.length > 0) {
      return themeCartAddUrl;
    }

    const root = (window.Shopify?.routes?.root || '/').toString();
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    return `${normalizedRoot}cart/add.js`;
  }

  /**
   * @returns {string}
   */
  #getCartChangeUrl() {
    const root = (window.Shopify?.routes?.root || '/').toString();
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    return `${normalizedRoot}cart/change.js`;
  }

  /**
   * @returns {HTMLFormElement | null}
   */
  #getProductForm() {
    const formId = this.refs.visibleProperty.getAttribute('form');
    if (!formId) return null;

    const form = document.getElementById(formId);
    if (!(form instanceof HTMLFormElement)) return null;

    return form;
  }

  /**
   * @param {boolean} enabled
   */
  #setPropertyInputsEnabled(enabled) {
    const propertyInputs = [
      this.refs.visibleProperty,
      this.refs.personalizedProperty,
      this.refs.engravingTextProperty,
      this.refs.engravingSlotProperty,
      this.refs.engravingConfigIdProperty,
      this.refs.bundleGroupIdProperty,
      this.refs.bundlePlanProperty,
      this.refs.previewVersionProperty,
    ];

    for (const input of propertyInputs) {
      input.disabled = !enabled;
    }
  }

  #clearPrimaryProperties() {
    this.refs.visibleProperty.value = '';
    this.refs.engravingTextProperty.value = '';
    this.refs.personalizedProperty.value = '';
    this.refs.engravingSlotProperty.value = '';
  }

  #clearBundleSlotProperties() {
    const propertyGroups = [
      this.refs.bundleVisibleSlotProperties,
      this.refs.bundleSlotTextProperties,
      this.refs.bundleSlotColorProperties,
      this.refs.bundleSlotConfigProperties,
    ];

    for (const propertyGroup of propertyGroups) {
      const inputs = Array.isArray(propertyGroup) ? propertyGroup : [];
      for (const input of inputs) {
        if (!(input instanceof HTMLInputElement)) continue;
        input.value = '';
        input.disabled = true;
      }
    }
  }

  /**
   * @param {string} message
   */
  #showInlineError(message) {
    const modalError = this.querySelector('[data-modal-error]');
    if (modalError instanceof HTMLElement && this.#isModalOpen) {
      modalError.textContent = message;
      modalError.hidden = false;
    }

    const errorContainer = this.#form?.closest('product-form-component')?.querySelector('[ref="addToCartTextError"]');
    if (!(errorContainer instanceof HTMLElement)) return;

    errorContainer.classList.remove('hidden');
    const textNode = errorContainer.childNodes[2];
    if (textNode) {
      textNode.textContent = message;
    } else {
      errorContainer.appendChild(document.createTextNode(message));
    }
  }

  #clearInlineError() {
    const modalError = this.querySelector('[data-modal-error]');
    if (modalError instanceof HTMLElement) {
      modalError.hidden = true;
      modalError.textContent = '';
    }

    const errorContainer = this.#form?.closest('product-form-component')?.querySelector('[ref="addToCartTextError"]');
    if (!(errorContainer instanceof HTMLElement)) return;

    errorContainer.classList.add('hidden');
  }

  /**
   * @param {string} value
   * @returns {boolean}
   */
  #hasValidCharacters(value) {
    return /^[\p{L}\p{N} .&+\-]*$/u.test(value);
  }

  /**
   * @param {number|string|undefined} value
   * @param {number} fallback
   * @returns {number}
   */
  #toNumber(value, fallback) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  /**
   * @returns {string}
   */
  #getFallbackConfigId() {
    const fallback = (this.dataset.fallbackConfigId || '').trim();
    return fallback || 'fixed_default';
  }

  /**
   * @returns {string}
   */
  #createBundleGroupId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return `bundle_${Date.now()}_${Math.random().toString(16).slice(2, 10)}`;
  }

  #updatePreviewTextScaling() {
    const previewText = /** @type {SVGTextElement | undefined} */ (this.refs.previewText);
    if (!previewText) return;

    previewText.removeAttribute('textLength');
    previewText.removeAttribute('lengthAdjust');

    if (this.#textMaxWidth <= 0) return;

    let bboxWidth = 0;
    try {
      bboxWidth = previewText.getBBox().width;
    } catch (err) {
      // getBBox might fail if the element is hidden (e.g., display: none)
    }

    if (bboxWidth > this.#textMaxWidth) {
      previewText.setAttribute('textLength', String(this.#textMaxWidth));
      previewText.setAttribute('lengthAdjust', 'spacingAndGlyphs');
    }
  }
}

if (!customElements.get('engraving-personalizer-component')) {
  customElements.define('engraving-personalizer-component', EngravingPersonalizerComponent);
}
